import { z } from "zod";
import { SPECIAL_FLAGS } from "../lib/specialFlags/registry";
import {
  InquiryAnswerIllustrationEnum,
  MeetingScoreAspectIdSchema,
  MeetingScoreAspectSchema,
  MeetingScoreCoverageSchema,
  MeetingScoreLevelSchema,
  MeetingScoreStatusSchema,
} from "../unizo/contract";
import {
  INQUIRY_FAQ_ANSWER_MAX_LENGTH,
  INQUIRY_FAQ_QUESTION_MAX_LENGTH,
  INQUIRY_LINKS_MAX,
  InquiryAnswerReferenceKindEnum,
  InquiryCategorySchema,
} from "../unizo/inquiry-answer";

/*
 * ONPPromptSettings テーブル
 * PK: SETTING#{settingId}  SK: VERSION#LATEST
 */
export const ONPPromptSettingIdSchema = z.enum([
  "LAYER1",
  "LAYER2",
  // PRODUCT_DEV-483: Layer 3a-base（商談記録ベースルール）をAdmin管理対象化
  "PRODUCT_BASE",
  "COMPANY_SEARCH",
  "DICTIONARY_TEMPLATE",
  "GROUP_CONTEXT",
  "GROUP_SUMMARY_GENERATION",
  "GROUP_SUMMARY_GENERATION_AI",
  "SPEED_MODE_GUIDE_GENERATION",
  "COMPANY_NAME_KANA_SUGGESTION",
  // PRODUCT_DEV-589 で GK を per-field 抽出＋グローバルチェックへ再設計したため、
  //   下記 MEETING_COMPLETION / MEETING_ITEM_CHECK は @deprecated（新フローで未使用）。
  //   enum は後方互換のため残置（stg/prod の DB 上書きは死蔵化。クリーンアップは
  //   dev/tools/PRODUCT_DEV-589/onp-cleanup.md 参照）。
  "MEETING_COMPLETION",
  // PRODUCT_DEV-480: 展開後の項目チェック（2nd pass）プロンプト（@deprecated PRODUCT_DEV-589）
  "MEETING_ITEM_CHECK",
  // PRODUCT_DEV-589: 自由記録モードのGK改善。フィールド（サブフィールド含む）単位の焦点抽出プロンプト
  "MEETING_FIELD_EXTRACTION",
  // PRODUCT_DEV-873: 議事録から商談記録を作るときだけ、MEETING_FIELD_EXTRACTION の末尾へ連結する
  // 箇条書きルール。上書きが無ければコードの定数（MEETING_FIELD_EXTRACTION_BULLET_STYLE）を使う
  "MEETING_FIELD_EXTRACTION_BULLET_STYLE",
  // PRODUCT_DEV-589: 抽出後のグローバルチェック（null救済＋配置/消失防止）プロンプト
  "MEETING_GLOBAL_CHECK",
  "MEETING_NL_SEARCH",
  "MEETING_NL_RERANK",
  "MEETING_NL_ANSWER",
  // PRODUCT_DEV-212: 商談編集完了時の担当者類似判定プロンプト
  "CONTACT_SIMILARITY_MATCH",
  // PRODUCT_DEV-475: 音声ログ整形プロンプト（フィラー・ハルシネーション除去）
  "TRANSCRIPT_CLEAN",
  // PRODUCT_DEV-527: CRM コピー文の文字数上限要約プロンプト
  "MEETING_COPY_SUMMARY",
  // PRODUCT_DEV-543: 個人(顧客マスタ)プロファイル抽出プロンプト（職業/特徴/年齢/収入/家族構成）
  "INDIVIDUAL_PROFILE_EXTRACTION",
  // PRODUCT_DEV-543: 個人向け商談の音声検索・確認ルール（COMPANY_SEARCH の個人版）
  "INDIVIDUAL_COMPANY_SEARCH",
  // PRODUCT_DEV-586: 議事録 構造化（要約/決定事項/Nextアクション）プロンプト
  "MINUTES_STRUCTURE",
  // PRODUCT_DEV-586: 議事録 チャンク文字起こしの文脈/語彙ヒントプロンプト
  "MINUTES_TRANSCRIBE",
  // PRODUCT_DEV-603: 議事録 文字起こしクリーニング（フィラー/ハルシネーション除去・推測禁止）プロンプト
  "MINUTES_CLEAN",
  // PRODUCT_DEV-634: 商談コピー時の指示部（法人 / 個人）。データ部（法人名・担当者・お客様情報の実値）は
  //   コード生成のままで、聞く/聞かないの指示だけを Admin から差し替えられるようにする。
  "COPY_MODE_COMPANY",
  "COPY_MODE_INDIVIDUAL",
  // PRODUCT_DEV-638 / PRODUCT_DEV-716: 対話キャラクターのトーン指示（全テナントの録音プロンプトへ常時注入）
  "CHARACTER_TONE",
  // PRODUCT_DEV-655 / PRODUCT_DEV-716: GateKeeper 第3コール（ヒアリングメモ抽出）。全テナントで実行
  "HEARING_NOTES_EXTRACTION",
  // PRODUCT_DEV-684: プリセット AI 作成（自由記録ベースの往復）。発話からプリセット草案 +
  // クローズドな確認質問を生成する。項目カタログは本文に含めず `{fieldCatalog}` を
  // コード側で置換する（content は 10000 字上限のためカタログは入らない）。
  "PRESET_DRAFT_BUILD",
  // PRODUCT_DEV-684(追補): プリセット音声修正。すでに使われているプリセットへの
  // 「ここをこう直したい」から**編集操作（ops）**を生成する。全体を返させないので
  // 触っていない箇所は変わらない。`{currentPreset}`/`{fieldCatalog}`/`{instruction}` を置換。
  "PRESET_VOICE_EDIT",
  // PRODUCT_DEV-612: 商談の振り返り。対象の商談のプリセットを土台に、ここの文を重ねて
  // 対話・抽出を動かす。Admin の機能「振り返り」で編集。
  //   MINUTES_REVIEW_PROMPT     … 対話のロール/ルール/会話例（Layer 3a 相当・プレースホルダなし）
  //   MINUTES_REVIEW_CONTEXT    … 議事録要約の埋め込み（Layer 3b 相当・
  //                               {minutesTitle}/{minutesDate}/{summary}/{decisions}/{nextActions}）
  //   MINUTES_REVIEW_EXTRACTION … 録音後 GateKeeper の「振り返り」項目の抽出指示
  "MINUTES_REVIEW_PROMPT",
  "MINUTES_REVIEW_CONTEXT",
  "MINUTES_REVIEW_EXTRACTION",
  // PRODUCT_DEV-872: 振り返りでコード固定だった文。上書きが無ければコードの定数を使う。
  //   MINUTES_REVIEW_TOOL_RULE     … ツール実行ルール（ツール名が消えた上書きは使わない）
  //   MINUTES_REVIEW_FOLLOWUP      … 記録のたびに送る聞き足しの文（{remainingPerspectives}・
  //                                  [[HAS_REMAINING]] / [[ALL_ASKED]]）
  //   MINUTES_REVIEW_PERSPECTIVES  … 共通の観点の「聞き方」「書くこと」（決まった書式。id と見出しは固定）
  //   MINUTES_REVIEW_KEYMAN_GUIDE  … キーマンの聞き方
  //   MINUTES_REVIEW_APPEND        … 2回目以降（追記モード）の文（{filledPerspectives}/{remainingPerspectives}）
  "MINUTES_REVIEW_TOOL_RULE",
  "MINUTES_REVIEW_FOLLOWUP",
  "MINUTES_REVIEW_PERSPECTIVES",
  "MINUTES_REVIEW_KEYMAN_GUIDE",
  "MINUTES_REVIEW_APPEND",
  // PRODUCT_DEV-673: 活動サマリ（日報/週報/月報）の本文下書きと、顧客ごとのハイライト。
  // 商談の extractedData とタイトルだけを入力にする（transcript 全文は送らない）。
  "ACTIVITY_SUMMARY_BODY",
  "ACTIVITY_SUMMARY_HIGHLIGHT",
  // PRODUCT_DEV-698: 部のまとめ（管理者ビューの先頭に出し、Polly が読み上げる）。
  // 読み上げ前提なので、箇条書き・記号・括弧書きを書かせない指示が要る。
  "TEAM_SUMMARY_BODY",
  // PRODUCT_DEV-1142 ③b: 部のまとめのまとめ方（フォロー重視・成果重視・活動量重視）ごとの観点。
  // TEAM_SUMMARY_BODY の末尾に足して使う（キーの正本は unizo 側の TEAM_SUMMARY_FOCUS_SETTING_IDS）。
  "TEAM_SUMMARY_FOCUS_FOLLOW",
  "TEAM_SUMMARY_FOCUS_RESULT",
  "TEAM_SUMMARY_FOCUS_ACTIVITY",
]);
export type ONPPromptSettingId = z.infer<typeof ONPPromptSettingIdSchema>;

export const DDBONPPromptSettingSchema = z.object({
  PK: z.string(), // SETTING#{settingId}
  SK: z.string(), // VERSION#LATEST (Phase2で履歴追加)
  settingId: ONPPromptSettingIdSchema,
  content: z.string().min(1).max(10000),
  // SHA-256 hex of content (UTF-8). PRODUCT_DEV-356: 環境間の偶然一致を防ぐため。
  // 既存レコードに無くてもよい (optional)。API 取得時に未保持なら計算して補完する。
  contentHash: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .optional(),
  updatedBy: z.string(),
  updatedAt: z.string(), // ISO 8601
  createdAt: z.string(), // ISO 8601
});
export type DDBONPPromptSetting = z.infer<typeof DDBONPPromptSettingSchema>;

export const UpdateONPPromptSettingSchema = z.object({
  content: z.string().min(1).max(10000),
});
export type UpdateONPPromptSetting = z.infer<
  typeof UpdateONPPromptSettingSchema
>;

/*
 * GlobalDictionary テーブル
 * PK: ENTRY#{entryId}  SK: ENTRY
 */
export const GlobalDictionaryCategorySchema = z.enum([
  "person_name",
  "product_service",
  "technical_term",
  "department_title",
  "other",
]);
export type GlobalDictionaryCategory = z.infer<
  typeof GlobalDictionaryCategorySchema
>;

export const DDBGlobalDictionaryEntrySchema = z.object({
  PK: z.string(), // ENTRY#{entryId}
  SK: z.string(), // ENTRY (固定値)
  entryId: z.string(),
  word: z.string().min(1),
  readings: z.array(z.string()),
  formalName: z.string().optional(),
  meaning: z.string().optional(),
  category: GlobalDictionaryCategorySchema,
  active: z.boolean(),
  updatedBy: z.string(),
  updatedAt: z.string(), // ISO 8601
  createdAt: z.string(), // ISO 8601
});
export type DDBGlobalDictionaryEntry = z.infer<
  typeof DDBGlobalDictionaryEntrySchema
>;

export const CreateGlobalDictionaryEntrySchema = z.object({
  word: z.string().min(1),
  readings: z.array(z.string()),
  formalName: z.string().optional(),
  meaning: z.string().optional(),
  category: GlobalDictionaryCategorySchema,
});
export type CreateGlobalDictionaryEntry = z.infer<
  typeof CreateGlobalDictionaryEntrySchema
>;

export const UpdateGlobalDictionaryEntrySchema = z.object({
  word: z.string().min(1).optional(),
  readings: z.array(z.string()).optional(),
  formalName: z.string().optional(),
  meaning: z.string().optional(),
  category: GlobalDictionaryCategorySchema.optional(),
  active: z.boolean().optional(),
});
export type UpdateGlobalDictionaryEntry = z.infer<
  typeof UpdateGlobalDictionaryEntrySchema
>;

/*
 * ReportPromptSettings（ONPPromptSettingsテーブルを共有）
 * PK: SETTING#{settingId}  SK: VERSION#LATEST
 */
/** プロンプト種別 */
export const REPORT_PROMPT_TYPES = ["SUMMARY", "EXTRACT", "DIALOGUE"] as const;
export type ReportPromptType = (typeof REPORT_PROMPT_TYPES)[number];

/** カテゴリID一覧 */
const REPORT_CATEGORY_IDS = [
  "CUSTOMER_PROFILE",
  "PAIN_POINTS",
  "HIDDEN_NEEDS",
  "BUDGET_RESOURCES",
  "COMPETITION",
  "RELATIONSHIPS",
  "DECISION_PROCESS",
  "RISKS",
] as const;

/** 全24種のsettingId */
const allReportSettingIds = REPORT_PROMPT_TYPES.flatMap((type) =>
  REPORT_CATEGORY_IDS.map(
    (cat) => `UNIZO_COMPANY_REPORT_${type}_${cat}` as const,
  ),
);

export const ReportPromptSettingIdSchema = z.enum(
  allReportSettingIds as unknown as [string, ...string[]],
);
export type ReportPromptSettingId = z.infer<typeof ReportPromptSettingIdSchema>;

/** settingId生成ヘルパー */
export function getReportPromptSettingId(
  type: ReportPromptType,
  categoryId: string,
): ReportPromptSettingId {
  return `UNIZO_COMPANY_REPORT_${type}_${categoryId.toUpperCase()}` as ReportPromptSettingId;
}

/** 全settingId一覧（API listAll フィルタ用） */
export const ALL_REPORT_PROMPT_SETTING_IDS = allReportSettingIds;

export const DDBReportPromptSettingSchema = z.object({
  PK: z.string(), // SETTING#{settingId}
  SK: z.string(), // VERSION#LATEST
  settingId: ReportPromptSettingIdSchema,
  content: z.string().min(1).max(50000),
  // SHA-256 hex of content (UTF-8). PRODUCT_DEV-356: ONP プロンプトと同じ運用方針。
  contentHash: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .optional(),
  updatedBy: z.string(),
  updatedAt: z.string(), // ISO 8601
  createdAt: z.string(), // ISO 8601
});
export type DDBReportPromptSetting = z.infer<
  typeof DDBReportPromptSettingSchema
>;

export const UpdateReportPromptSettingSchema = z.object({
  content: z.string().min(1).max(50000),
});
export type UpdateReportPromptSetting = z.infer<
  typeof UpdateReportPromptSettingSchema
>;

/*
 * OperatorAnnouncements テーブル
 * PK: ANNOUNCEMENT#{announcementId}  SK: ANNOUNCEMENT
 * 運営からのお知らせ。Adminが管理し、各プロダクトが targetProducts でフィルタして取得する。
 */
export const OperatorAnnouncementCategorySchema = z.enum([
  "urgent",
  "maintenance",
  "other",
]);
export type OperatorAnnouncementCategory = z.infer<
  typeof OperatorAnnouncementCategorySchema
>;

// PRODUCT_DEV-749: Minizo（メモ・ToDo だけのデモ版・PRODUCT_DEV-688）を追加。
// Minizo は Unizo と同じ API を共有するが**お知らせは完全共有にしない**。
// 両方に出したいものは "all"、Unizo にだけ出したいものは "unizo" を選ぶ。
// ⚠ 配信 UI（apps/admin）側の選択肢追加は別課題。ここに足しただけでは運営が選べない。
export const OperatorAnnouncementTargetProductSchema = z.enum([
  "unizo",
  "minizo",
  "portal",
  "admin",
  "all",
]);
export type OperatorAnnouncementTargetProduct = z.infer<
  typeof OperatorAnnouncementTargetProductSchema
>;

export const MAX_PINNED_OPERATOR_ANNOUNCEMENTS = 3;

export const DDBOperatorAnnouncementSchema = z.object({
  PK: z.string(), // ANNOUNCEMENT#{announcementId}
  SK: z.string(), // ANNOUNCEMENT (固定値)
  announcementId: z.string(),
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(10000),
  linkUrl: z.string().url().nullable(),
  category: OperatorAnnouncementCategorySchema,
  targetProducts: z.array(OperatorAnnouncementTargetProductSchema).min(1),
  publishStartAt: z.string(), // ISO 8601 - ニュース一覧の掲載期間 開始
  publishEndAt: z.string(), // ISO 8601 - ニュース一覧の掲載期間 終了
  isPinned: z.boolean(),
  displayOrder: z.number().int().min(0),
  active: z.boolean(),
  // PRODUCT_DEV-471 拡張: ピン(HOMEカルーセル)掲載期間。
  // 未設定なら publishStartAt/publishEndAt にフォールバック (既存レコード互換)。
  // ニュース期間とは独立に設定可。
  pinStartAt: z.string().optional(), // ISO 8601
  pinEndAt: z.string().optional(), // ISO 8601
  // PRODUCT_DEV-466 拡張: 添付ファイル (SharedFiles の id 配列、後方互換のため optional)
  fileIds: z.array(z.string()).optional(),
  // PRODUCT_DEV-466 拡張: API レスポンス時のみ Unizo API が fileIds を解決して埋める (DDB には保存されない)
  files: z
    .array(
      z.object({
        id: z.string(),
        fileName: z.string(),
        mimeType: z.string(),
        sizeBytes: z.number(),
        uploadedAt: z.string(),
      }),
    )
    .optional(),
  createdBy: z.string(),
  updatedBy: z.string(),
  createdAt: z.string(), // ISO 8601
  updatedAt: z.string(), // ISO 8601
});
export type DDBOperatorAnnouncement = z.infer<
  typeof DDBOperatorAnnouncementSchema
>;

export const CreateOperatorAnnouncementSchema = z.object({
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(10000),
  linkUrl: z.string().url().nullable().optional(),
  category: OperatorAnnouncementCategorySchema,
  targetProducts: z.array(OperatorAnnouncementTargetProductSchema).min(1),
  publishStartAt: z.string(),
  publishEndAt: z.string(),
  isPinned: z.boolean().optional(),
  displayOrder: z.number().int().min(0).optional(),
  // PRODUCT_DEV-471: ピン期間 (未指定なら publish 期間を流用)
  pinStartAt: z.string().optional(),
  pinEndAt: z.string().optional(),
  fileIds: z.array(z.string()).optional(),
});
export type CreateOperatorAnnouncement = z.infer<
  typeof CreateOperatorAnnouncementSchema
>;

export const UpdateOperatorAnnouncementSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  body: z.string().min(1).max(10000).optional(),
  linkUrl: z.string().url().nullable().optional(),
  category: OperatorAnnouncementCategorySchema.optional(),
  targetProducts: z
    .array(OperatorAnnouncementTargetProductSchema)
    .min(1)
    .optional(),
  publishStartAt: z.string().optional(),
  publishEndAt: z.string().optional(),
  isPinned: z.boolean().optional(),
  displayOrder: z.number().int().min(0).optional(),
  active: z.boolean().optional(),
  // PRODUCT_DEV-471: ピン期間 (空文字列でクリア = publish 期間にフォールバック)
  pinStartAt: z.string().optional(),
  pinEndAt: z.string().optional(),
  fileIds: z.array(z.string()).optional(),
});
export type UpdateOperatorAnnouncement = z.infer<
  typeof UpdateOperatorAnnouncementSchema
>;

export const OperatorAnnouncementListResponseSchema = z.object({
  announcements: z.array(DDBOperatorAnnouncementSchema),
});
export type OperatorAnnouncementListResponse = z.infer<
  typeof OperatorAnnouncementListResponseSchema
>;

/*
 * OperatorAnnouncementReads テーブル
 * PK: USER#{userId}#TENANT#{tenantId}  SK: ANNOUNCEMENT#{announcementId}
 * ユーザーごとの運営お知らせ既読状態
 */
export const DDBOperatorAnnouncementReadSchema = z.object({
  PK: z.string(),
  SK: z.string(),
  userId: z.string(),
  tenantId: z.string(),
  announcementId: z.string(),
  readAt: z.string(), // ISO 8601
});
export type DDBOperatorAnnouncementRead = z.infer<
  typeof DDBOperatorAnnouncementReadSchema
>;

export const OperatorAnnouncementReadsResponseSchema = z.object({
  readAnnouncementIds: z.array(z.string()),
});
export type OperatorAnnouncementReadsResponse = z.infer<
  typeof OperatorAnnouncementReadsResponseSchema
>;

export const MarkOperatorAnnouncementsReadSchema = z.object({
  announcementIds: z.array(z.string()).min(1).max(100),
});
export type MarkOperatorAnnouncementsRead = z.infer<
  typeof MarkOperatorAnnouncementsReadSchema
>;

/*
 * NotificationTemplates テーブル (PRODUCT_DEV-379)
 * PK: TEMPLATE#{templateId}  SK: VERSION#LATEST
 * 通知メッセージ本文テンプレートをグローバル（全テナント共通）で管理する。
 * AIプロンプトではないため ONPPromptSettings とは別テーブル。
 */
export const NotificationDestinationTypeSchema = z.enum([
  "teams",
  // 将来: "slack", "email"
]);
export type NotificationDestinationType = z.infer<
  typeof NotificationDestinationTypeSchema
>;

export const NotificationTemplateIdSchema = z.enum(["teams-keyword-hit"]);
export type NotificationTemplateId = z.infer<
  typeof NotificationTemplateIdSchema
>;

export const DDBNotificationTemplateSchema = z.object({
  PK: z.string(), // TEMPLATE#{templateId}
  SK: z.string(), // VERSION#LATEST
  templateId: NotificationTemplateIdSchema,
  destinationType: NotificationDestinationTypeSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  bodyTemplate: z.string().min(1).max(20000),
  variables: z.array(z.string()),
  updatedBy: z.string(),
  updatedAt: z.string(),
  createdAt: z.string(),
});
export type DDBNotificationTemplate = z.infer<
  typeof DDBNotificationTemplateSchema
>;

export const UpdateNotificationTemplateSchema = z.object({
  bodyTemplate: z.string().min(1).max(20000),
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(1000).optional(),
});
export type UpdateNotificationTemplate = z.infer<
  typeof UpdateNotificationTemplateSchema
>;

export const TestSendNotificationRequestSchema = z.object({
  destinationType: NotificationDestinationTypeSchema,
  webhookUrl: z.string().url(),
  bodyTemplate: z.string().min(1).max(20000),
  sampleVariables: z.record(z.string(), z.string()),
});
export type TestSendNotificationRequest = z.infer<
  typeof TestSendNotificationRequestSchema
>;

export const TestSendNotificationResponseSchema = z.object({
  success: z.boolean(),
  statusCode: z.number().optional(),
  error: z.string().optional(),
});
export type TestSendNotificationResponse = z.infer<
  typeof TestSendNotificationResponseSchema
>;

/*
 * AgenticJobs (AutopilotSessions テーブル拡張) - PRODUCT_DEV-413
 * PK: JOB#{jobId}  SK: LATEST
 * Admin UI から登録するエージェンティック AI ジョブ。
 * Strands Agents (Lambda Container) または CC CLI (Single EC2, start/stop) で実行される。
 * Phase 1 原則: AI = Read-Only。
 */
export const AgenticJobTypeSchema = z.enum(["strands-agent", "cc-cli-batch"]);
export type AgenticJobType = z.infer<typeof AgenticJobTypeSchema>;

/*
 * Job 分類 (PRODUCT_DEV-413 Phase 2.x):
 *  - task     : ジョブ = タスク 1 件。JobExpander が JobTasks に 1 件 PutItem。
 *  - workflow : 固定ワークフロー定義から複数タスクに展開。
 *               JobExpander が AgenticWorkflows table から workflowId で取得し
 *               tasks[] を JobTasks に batch_put。{{var}} テンプレ展開も実施。
 *  - agent    : Plan Agent が動的にタスクを生成。
 *               JobExpander が Plan Agent (initial-plan) を async invoke。
 *
 * 未設定 (optional) の場合、JobExpander は旧 jobType + maxIterations から
 * legacy 互換ブランチで上記いずれかに割り当てる。
 */
export const JobKindSchema = z.enum(["task", "workflow", "agent"]);
export type JobKind = z.infer<typeof JobKindSchema>;

/*
 * Self-Loop phase - PRODUCT_DEV-413 self-loop extension
 * Backlog webhook → implement → waiting-deploy → playwright → retry/human-handoff の
 * ライフサイクル状態を表す。Strands / 既存 cc-cli-batch ジョブには phase 未設定で OK
 * (job-loop.sh は phase 未設定なら legacy path にフォールバックする)。
 */
export const SelfLoopPhaseSchema = z.enum([
  "implement",
  "waiting-deploy",
  "playwright",
  "retry",
  "human-handoff",
]);
export type SelfLoopPhase = z.infer<typeof SelfLoopPhaseSchema>;

export const AgenticJobStatusSchema = z.enum([
  "pending",
  "running",
  "paused",
  "completed",
  "failed",
  "cancelled",
  // PRODUCT_DEV-413 multi-agent orchestrator: Plan→Orchestrate→Worker→Judge cycle.
  // 既存ジョブは "running" のままで動作し、orchestrator対応ジョブのみ以下を使う。
  "planning",
  "executing",
  "replanning",
  "killed",
]);
export type AgenticJobStatus = z.infer<typeof AgenticJobStatusSchema>;

export const AgenticJobOutputDestinationSchema = z.enum([
  "admin",
  "backlog-comment",
  "slack-dm",
  // PRODUCT_DEV-438 Phase2: Slack メンション/ショートカット起点ジョブの結果を
  // 起動元スレッドへ返信する宛先。slackContext{channel,threadTs} を併せて設定する。
  "slack-thread-reply",
]);
export type AgenticJobOutputDestination = z.infer<
  typeof AgenticJobOutputDestinationSchema
>;

export const AgenticJobBacklogContextSchema = z.object({
  issueKey: z.string().optional(),
  commentId: z.number().int().optional(),
  // PRODUCT_DEV-831: 梅子を起動した人の Backlog userId (webhook payload の
  // createdUser.id = 「担当者/ステータスを変えた人」)。権限判定の入力であり、
  // 「誰に担当が変わったか」を表す sourceIssueAssigneeId とは別物。
  requesterUserId: z.number().int().positive().optional(),
});
export type AgenticJobBacklogContext = z.infer<
  typeof AgenticJobBacklogContextSchema
>;

/*
 * PRODUCT_DEV-438 Phase2: Slack 起点ジョブのコンテキスト。
 * SlackEventHandler → Router Worker が起動元スレッド (channel/threadTs) を保持し、
 * outputDestination="slack-thread-reply" の WriteProposal で元スレッドに返信する。
 *  - source: 起動経路。mention=自由依頼 (router が jobKind 判定) / shortcut=固定の課題化。
 */
export const AgenticJobSlackContextSchema = z.object({
  channel: z.string().min(1),
  threadTs: z.string().min(1),
  source: z.enum(["mention", "shortcut"]).optional(),
  // PRODUCT_DEV-831: 梅子を起動した人の Slack userId (app_mention の event.user)。
  // SlackEventHandler → SQS までは元々運ばれていたが Worker で捨てられていた。
  // 権限判定の入力なので、ジョブに残して監査できるようにする。
  userId: z.string().optional(),
  // PRODUCT_DEV-831: 梅子を呼んだ発言そのものの ts (app_mention の event.ts)。
  // threadTs はスレッドの親で別物。仕事が終わったときの ✅ / 🙇 をこの発言に付ける。
  // 未設定 (この項目より前のジョブ) なら threadTs で代用する。
  eventTs: z.string().optional(),
});
export type AgenticJobSlackContext = z.infer<
  typeof AgenticJobSlackContextSchema
>;

export const AgenticJobModelUsageSchema = z.record(
  z.string(),
  z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    // PRODUCT_DEV-413 可観測性: prompt cache から読まれたトークン数 (任意)。
    // 未設定は「計測なし/0」として扱う (後方互換)。cache 効果の可視化に使う。
    cacheReadTokens: z.number().int().nonnegative().optional(),
  }),
);
export type AgenticJobModelUsage = z.infer<typeof AgenticJobModelUsageSchema>;

export const AgenticJobCheckpointSchema = z
  .object({
    s3Key: z.string(),
    savedAt: z.string(),
  })
  .passthrough();
export type AgenticJobCheckpoint = z.infer<typeof AgenticJobCheckpointSchema>;

export const DEFAULT_AGENTIC_JOB_COST_LIMIT_USD = 5;
export const MAX_AGENTIC_JOB_COST_LIMIT_USD = 100;

export const DEFAULT_AGENTIC_JOB_MAX_ITERATIONS = 5;
export const MAX_AGENTIC_JOB_MAX_ITERATIONS = 20;

/*
 * PRODUCT_DEV-413 Phase B: 成果物の出力フォーマット。
 *  - markdown : 従来通り result (テキスト) として返す (既定)。
 *  - csv      : finalize がデータを CSV ファイルとして生成し artifacts[] に追記。
 *  - excel    : finalize がデータを xlsx ファイルとして生成し artifacts[] に追記。
 * 未設定 (optional) の場合は markdown とみなす (後方互換)。
 */
export const OutputFormatSchema = z.enum(["markdown", "csv", "excel"]);
export type OutputFormat = z.infer<typeof OutputFormatSchema>;

/*
 * PRODUCT_DEV-413 Phase B: ジョブが生成したファイル成果物 1 件のメタデータ。
 * ファイル本体は ops の S3 (umeee-autopilot-sessions-${env}) の
 * artifacts/{jobId}/<uuid>-<name> に保管し、ここにはメタのみ記録する。
 * DL は Admin の presigned URL 経由 (SharedFiles の generateSharedFileDownloadUrl 流用)。
 */
export const JobArtifactSchema = z.object({
  name: z.string().min(1).max(255), // 表示用ファイル名 (例: summary.xlsx)
  s3Key: z.string().min(1), // artifacts/{jobId}/<uuid>-<name>
  contentType: z.string().min(1).max(255),
  size: z.number().int().nonnegative(),
  createdAt: z.string(), // ISO 8601
});
export type JobArtifact = z.infer<typeof JobArtifactSchema>;

/*
 * PRODUCT_DEV-831: agentRole の値そのもの。各ロールの説明つきの正本は下の
 * JobTasks セクションの AgentRoleSchema だが、DDBAgenticJobSchema がそれより
 * 手前で allowedAgentRoles を参照するため、値の配列だけをここへ切り出す。
 * ロールを増減するときは、この配列 1 か所だけを直せばよい。
 */
export const AGENT_ROLES = [
  "investigate-light",
  "investigate",
  "code-change",
  "playwright-test",
  "refactor",
  "finalize",
  // PRODUCT_DEV-413 self-loop extension
  "self-loop-implement",
  "self-loop-playwright",
] as const;

/*
 * PRODUCT_DEV-831: 梅子 (CC バッチ) を呼んだ人の権限階層。
 *  - business  : 調査・要約・資料作成・外部投稿まで (read-only ロール + finalize)
 *  - developer : 上記 + コード変更と PR 提案。develop へのマージは含めない
 * tier から許可 agentRole への対応は admin/agentic-requester.ts が正本。
 */
export const RequesterTierSchema = z.enum(["business", "developer"]);
export type RequesterTier = z.infer<typeof RequesterTierSchema>;

export const DDBAgenticJobSchema = z.object({
  PK: z.string(),
  SK: z.string(),
  jobId: z.string(),
  jobType: AgenticJobTypeSchema,
  status: AgenticJobStatusSchema,
  prompt: z.string().min(1).max(20000),
  backlogContext: AgenticJobBacklogContextSchema.optional(),
  // PRODUCT_DEV-438 Phase2: Slack 起点ジョブのみ設定 (mention/shortcut)。
  // outputDestination="slack-thread-reply" の返信先を保持する (後方互換 optional)。
  slackContext: AgenticJobSlackContextSchema.optional(),
  outputDestination: AgenticJobOutputDestinationSchema,
  tenantScope: z.array(z.string()).min(1),
  costLimitUsd: z.number().positive().max(MAX_AGENTIC_JOB_COST_LIMIT_USD),
  actualCostUsd: z.number().nonnegative().default(0),
  modelUsage: AgenticJobModelUsageSchema.default({}),
  // PRODUCT_DEV-413: マルチエージェント経路の total-burn 集計 (リトライ分込み)。
  // actualCostUsd が authoritative な総額。burnedCostUsd は内訳確認用に別途累積。
  burnedCostUsd: z.number().nonnegative().optional(),
  // Plan Agent (initial-plan / judge) 自身の Bedrock コスト。Orchestrator が
  // actualCostUsd に畳み込む。planAgentInvocations インクリメントと同時に ADD。
  planAgentCostUsd: z.number().nonnegative().optional(),
  planAgentModelUsage: AgenticJobModelUsageSchema.optional(),
  result: z.string().optional(),
  resultS3Key: z.string().optional(),
  // PRODUCT_DEV-413 Phase B: 出力フォーマット (未設定なら markdown 扱い)。
  outputFormat: OutputFormatSchema.optional(),
  // PRODUCT_DEV-413 Phase B: ファイル成果物 (Excel/CSV 等)。
  // runner の upload-artifact.sh が list_append で追記する。
  artifacts: z.array(JobArtifactSchema).optional(),
  // PRODUCT_DEV-413 Phase B2: jobKind=workflow で WF に ready な format テンプレが
  // 紐づくとき、JobExpander が template の S3 key を job に転記する。finalize task は
  // これがあれば template を mapping で埋めて Excel 成果物を生成する。
  formatTemplateS3Key: z.string().optional(),
  error: z.string().optional(),
  checkpoint: AgenticJobCheckpointSchema.optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // PRODUCT_DEV-831: Slack / Backlog から起動されたジョブの権限 (optional, backward
  // compatible)。Admin UI 経由のジョブは denyIfNotAgenticUser が別途効いているため
  // 未設定で、その場合 Orchestrator は agentRole を制限しない。
  //  - requesterTier      : 起動者の階層 (監査・表示用)
  //  - allowedAgentRoles  : このジョブで dispatch してよい agentRole。Orchestrator が
  //                         これに無いロールのタスクを skipped にする最終関所。
  requesterTier: RequesterTierSchema.optional(),
  allowedAgentRoles: z.array(z.enum(AGENT_ROLES)).min(1).optional(),
  // PRODUCT_DEV-831: 利用者が実際に書いた依頼文そのもの (Slack のメンション本文 /
  // Backlog の最新コメント)。prompt にはスレッド全文や課題詳細も入るため、
  // 「利用者が何を頼んだか」だけを取り出して別に持つ。
  // Orchestrator が dispatch payload に載せ、各ワーカーのプロンプトの先頭に置く。
  // **ワーカーは prompt を読めず instructions しか受け取らない**ので、これが無いと
  // Planner が書き落とした指定 (「3 行で」等) は誰にも届かない。
  requestText: z.string().optional(),
  // PRODUCT_DEV-831: 回答が相手への質問を含み、返答待ちかどうか (judge が判定)。
  // Backlog 起点のジョブで終了後のステータスを決めるのに使う
  // (true→依頼中に戻して人へ / false→処理済み)。
  needsUserInput: z.boolean().optional(),
  // PRODUCT_DEV-413 multi-agent orchestrator フィールド (optional, backward compatible).
  // 既存ジョブはこれらが無くても従来通り動作する。
  // orchestrator対応ジョブのみ Plan Agent が initial-plan/judge で更新する。
  currentIteration: z.number().int().nonnegative().optional(),
  maxIterations: z
    .number()
    .int()
    .positive()
    .max(MAX_AGENTIC_JOB_MAX_ITERATIONS)
    .optional(),
  planAgentInvocations: z.number().int().nonnegative().optional(),
  startedAt: z.string().optional(),
  // PRODUCT_DEV-413 self-loop extension (optional, backward compatible).
  // BacklogWebhookHandler / Admin API self-loop kind から登録されたジョブのみ設定する。
  // phase 未設定なら job-loop.sh は legacy path で処理する。
  phase: SelfLoopPhaseSchema.optional(),
  issueKey: z
    .string()
    .regex(/^[A-Z][A-Z0-9_]*-\d+$/)
    .optional(),
  parentIssueKey: z
    .string()
    .regex(/^[A-Z][A-Z0-9_]*-\d+$/)
    .optional(),
  sourceIssueAssigneeId: z.number().int().positive().optional(),
  releaseBranch: z.string().max(200).optional(),
  prHeadBranch: z.string().max(200).optional(),
  prNumbers: z.array(z.number().int().positive()).optional(),
  mergedAt: z.string().optional(),
  playwrightAttemptCount: z.number().int().nonnegative().optional(),
  // PRODUCT_DEV-413 Phase 2.x: Job 分類。新規ジョブは Admin UI から jobKind が
  // 必ず付与される。既存ジョブは optional のままで、JobExpander の legacy 互換
  // ブランチで旧 jobType + maxIterations から割り当てる。
  jobKind: JobKindSchema.optional(),
  // jobKind = "workflow" のとき必須。AgenticWorkflows table の workflowId を指す。
  workflowId: z.string().min(1).max(64).optional(),
  // タスク 1 件あたりの最大 attempt 回数 (orchestrator 側で enforce)。
  // 未設定なら DEFAULT_JOB_TASK_MAX_ATTEMPTS (= 10) を使う。
  // heartbeat 等の "1 回だけで十分" のジョブは 1 を明示して再実行を抑止する。
  maxAttempts: z.number().int().positive().max(100).optional(),
});
export type DDBAgenticJob = z.infer<typeof DDBAgenticJobSchema>;

/**
 * JobTask attemptCount の Hard cap (orchestrator が tryDispatchTask / sweep で参照)。
 * job.maxAttempts または task.maxAttempts が指定されていればそちらが優先される。
 * 401 などで stuck → sweep → re-dispatch ループが暴走するのを防ぐための最後の砦。
 */
export const DEFAULT_JOB_TASK_MAX_ATTEMPTS = 10;

export const CreateAgenticJobSchema = z.object({
  jobType: AgenticJobTypeSchema,
  prompt: z.string().min(1).max(20000),
  backlogContext: AgenticJobBacklogContextSchema.optional(),
  outputDestination: AgenticJobOutputDestinationSchema,
  costLimitUsd: z
    .number()
    .positive()
    .max(MAX_AGENTIC_JOB_COST_LIMIT_USD)
    .optional(),
  // PRODUCT_DEV-413 multi-agent orchestrator: 未指定なら1 (= 従来通り単発実行)。
  // 2以上を指定すると Plan Agent が DAG 分解して orchestrator 経由で実行される。
  maxIterations: z
    .number()
    .int()
    .positive()
    .max(MAX_AGENTIC_JOB_MAX_ITERATIONS)
    .optional(),
  // PRODUCT_DEV-413 Phase 2.x: Job 分類。新規 UI 経路では task/workflow/agent
  // のいずれかを必ず送るが、互換性のため optional。
  jobKind: JobKindSchema.optional(),
  // jobKind = "workflow" のとき必須 (Admin API 側で追加 validation)。
  workflowId: z.string().min(1).max(64).optional(),
  // PRODUCT_DEV-413 Phase B: 成果物の出力フォーマット (未指定なら markdown)。
  outputFormat: OutputFormatSchema.optional(),
});
export type CreateAgenticJob = z.infer<typeof CreateAgenticJobSchema>;

/*
 * Self-Loop job creation - PRODUCT_DEV-413 self-loop extension
 * Admin UI / Backlog webhook の両方から self-loop ジョブを登録するための入力スキーマ。
 * 内部で createSelfLoopJob factory が DDBAgenticJobSchema 形式に展開する。
 */
export const CreateSelfLoopJobSchema = z.object({
  issueKey: z.string().regex(/^[A-Z][A-Z0-9_]*-\d+$/),
  // 省略時は Backlog API で issue.assignee.id を fetch
  sourceIssueAssigneeId: z.number().int().positive().optional(),
  // 省略時は Backlog API で issue.parentIssueId から自動解決
  parentIssueKey: z
    .string()
    .regex(/^[A-Z][A-Z0-9_]*-\d+$/)
    .optional(),
  // 省略時は self-loop-implement skill が親課題コメントから AI 抽出
  releaseBranch: z.string().max(200).optional(),
  // 省略時は DEFAULT_AGENTIC_JOB_COST_LIMIT_USD
  costLimitUsd: z
    .number()
    .positive()
    .max(MAX_AGENTIC_JOB_COST_LIMIT_USD)
    .optional(),
});
export type CreateSelfLoopJob = z.infer<typeof CreateSelfLoopJobSchema>;

export const AgenticJobListResponseSchema = z.object({
  jobs: z.array(DDBAgenticJobSchema),
});
export type AgenticJobListResponse = z.infer<
  typeof AgenticJobListResponseSchema
>;

/*
 * AgenticWriteEvents (Phase 2: Append-Only Write via Writer pattern) - PRODUCT_DEV-413
 * PK: PROPOSAL#{proposalId}  SK: LATEST
 * AI Agent (Strands / CC CLI) は外部システムへ直接書き込まず、本テーブルへ
 * WriteProposal を PutItem する。DDB Streams INSERT で Writer Lambda が起動し、
 * schema validate / allow-list / tenantId / rate cap を確認後に実 API を呼ぶ。
 * Phase 2 で許可される target: backlog-comment / slack-message のみ (append-only)。
 * PRODUCT_DEV-438 Phase2 で slack-thread-reply / backlog-issue-create を追加
 * (いずれも append-only。スレッド返信・新規課題起票)。
 */
export const AgenticWriteTargetSchema = z.enum([
  "backlog-comment",
  "slack-message",
  // PRODUCT_DEV-413: 軽微なコード改修PR提案。AI は patch を S3 にアップロードし
  // proposal を発行するのみ。実際の PR 作成は pr-proposal-writer Lambda が
  // GitHub App 認証で実行する。Layer 5 guardrails (path deny/allow, binary 禁止,
  // ベースブランチ allow-list, rate 上限) は Writer Lambda で強制。
  "github-pr",
  // PRODUCT_DEV-438 Phase2: Slack 起点ジョブの結果を起動元スレッドへ返信する
  // (固定 webhook channel への slack-message とは別。channel/threadTs 指定)。
  "slack-thread-reply",
  // PRODUCT_DEV-438 Phase2: 新規 Backlog 課題を起票する (既存 issue への
  // コメント追記 backlog-comment とは別。append-only=新規作成のみ)。
  "backlog-issue-create",
  // PRODUCT_DEV-564 kintone連携 Step 1: 顧客の kintone アプリへレコードを1件登録する。
  // Writer が {subdomain}.cybozu.com を host allow-list 検証のうえ REST で作成。
  // 顧客データ書き込みのため承認ゲート対象 (status=approved でのみ実行)。
  "kintone-record-create",
  // PRODUCT_DEV-564 Step 1: Teams Incoming Webhook にメッセージを投稿する
  // (per-tenant notificationWebhook とは別。WriteProposal 経由の agentic 書き込み)。
  // 顧客向け投稿のため承認ゲート対象。
  "teams-message",
]);
export type AgenticWriteTarget = z.infer<typeof AgenticWriteTargetSchema>;

// PRODUCT_DEV-564 Step 1: 承認ゲート対象 target の一覧と実行ゲート判定は
// @shared-domain/lib/write-proposal/gate に集約 (純粋・単体テスト可能)。

export const BacklogCommentPayloadSchema = z.object({
  type: z.literal("backlog-comment"),
  issueKey: z.string().regex(/^[A-Z][A-Z0-9_]*-\d+$/),
  content: z.string().min(1).max(20000),
  notifiedUserIds: z.array(z.number().int().positive()).max(10).default([]),
  // PRODUCT_DEV-831: コメントに添付する成果物 (HTML / pptx 等) の S3 key。
  // upload-artifact.sh が付ける `artifacts/{jobId}/<uuid>.<ext>` 形式のみ受け付け、
  // Writer 側で **proposal の jobId と一致する prefix か**を必ず検証する
  // (他ジョブの成果物を持ち出せないようにするため)。
  attachmentS3Keys: z
    .array(z.string().min(1).max(1024))
    .max(5)
    .optional(),
});
export type BacklogCommentPayload = z.infer<typeof BacklogCommentPayloadSchema>;

export const SlackMessagePayloadSchema = z.object({
  type: z.literal("slack-message"),
  text: z.string().min(1).max(4000),
});
export type SlackMessagePayload = z.infer<typeof SlackMessagePayloadSchema>;

/*
 * Slack thread reply payload - PRODUCT_DEV-438 Phase2
 * Slack 起点ジョブの結果を起動元スレッドに返信する。固定 webhook channel へ投稿する
 * slack-message と異なり、channel / threadTs を明示して chat.postMessage で返す。
 * write-proposal-writer Lambda が Bot token で実行する (append-only=投稿のみ)。
 */
export const SlackThreadReplyPayloadSchema = z.object({
  type: z.literal("slack-thread-reply"),
  channel: z.string().min(1),
  threadTs: z.string().min(1),
  text: z.string().min(1).max(4000),
});
export type SlackThreadReplyPayload = z.infer<
  typeof SlackThreadReplyPayloadSchema
>;

/*
 * Backlog issue create payload - PRODUCT_DEV-438 Phase2
 * 新規 Backlog 課題を起票する (既存 issue へのコメント追記 backlog-comment とは別)。
 * projectId / issueTypeId / priorityId 未指定時は Writer 側の既定 (ops 既定の
 * プロジェクト/タスク種別/優先度中) を使う。append-only=新規作成のみ。
 */
export const BacklogIssueCreatePayloadSchema = z.object({
  type: z.literal("backlog-issue-create"),
  summary: z.string().min(1).max(255),
  description: z.string().min(1).max(20000),
  projectId: z.string().optional(),
  issueTypeId: z.string().optional(),
  priorityId: z.string().optional(),
});
export type BacklogIssueCreatePayload = z.infer<
  typeof BacklogIssueCreatePayloadSchema
>;

/*
 * GitHub PR proposal payload - PRODUCT_DEV-413 Phase 1+
 * AI 側 (CC CLI Worker) は worktree で編集後、`git format-patch` で patch を作成し
 * S3 (sessionsBucket/patches/<jobId>/<proposalId>.patch) にアップロードしてから
 * 本 payload で proposal を発行する。
 * pr-proposal-writer Lambda が DDB Streams で起動し、Layer 5 guardrails を
 * 適用してから GitHub App 認証で PR を作成する。
 *
 * ベースブランチ: develop / release-YYYYMMDD-vX.Y[-name] のみ許可。
 *                main / staging / hotfix/* は Writer Lambda 側で reject。
 * headBranch: `agentic/` prefix 必須。Layer 5 で再検証される。
 */
export const GitHubPrPayloadSchema = z.object({
  type: z.literal("github-pr"),
  issueKey: z.string().regex(/^[A-Z][A-Z0-9_]*-\d+$/),
  baseBranch: z.string().min(1).max(120),
  headBranch: z
    .string()
    .min(1)
    .max(200)
    .regex(/^agentic\//),
  title: z.string().min(1).max(200),
  summary: z.string().min(1).max(8000),
  patchS3Key: z.string().min(1).max(500),
  loc: z.number().int().nonnegative(),
  fileCount: z.number().int().nonnegative(),
});
export type GitHubPrPayload = z.infer<typeof GitHubPrPayloadSchema>;

/*
 * kintone record create payload - PRODUCT_DEV-564 Step 1
 * 顧客の kintone アプリへレコードを1件登録する。record は kintone REST の
 * フィールド形式 ({ フィールドコード: { value } })。dedupeKey 指定時は Writer が
 * 事前に同値レコードを検索し、存在すれば作成をスキップ (二重登録防止=冪等)。
 * 承認ゲート対象 (status=approved でのみ実行)。
 */
export const KintoneRecordCreatePayloadSchema = z.object({
  type: z.literal("kintone-record-create"),
  appId: z.union([z.string().min(1).max(20), z.number().int().positive()]),
  record: z.record(z.string().min(1), z.object({ value: z.unknown() })),
  dedupeKey: z
    .object({
      // field は kintone フィールドコード。dedupe クエリへ生挿入されるため、
      // クエリメタ文字 (空白 / 引用符 / 括弧 / 比較演算子 / カンマ) を禁止して
      // クエリインジェクションを防ぐ (PRODUCT_DEV-564 レビュー M-1)。
      field: z
        .string()
        .min(1)
        .max(128)
        .regex(/^[^\s"'()=<>,]+$/),
      value: z.string().min(1).max(1024),
    })
    .optional(),
});
export type KintoneRecordCreatePayload = z.infer<
  typeof KintoneRecordCreatePayloadSchema
>;

/*
 * Teams message payload - PRODUCT_DEV-564 Step 1
 * Teams Incoming Webhook にメッセージを投稿する。承認ゲート対象。
 */
export const TeamsMessagePayloadSchema = z.object({
  type: z.literal("teams-message"),
  text: z.string().min(1).max(4000),
  title: z.string().min(1).max(200).optional(),
});
export type TeamsMessagePayload = z.infer<typeof TeamsMessagePayloadSchema>;

export const AgenticWritePayloadSchema = z.discriminatedUnion("type", [
  BacklogCommentPayloadSchema,
  SlackMessagePayloadSchema,
  SlackThreadReplyPayloadSchema,
  BacklogIssueCreatePayloadSchema,
  GitHubPrPayloadSchema,
  KintoneRecordCreatePayloadSchema,
  TeamsMessagePayloadSchema,
]);
export type AgenticWritePayload = z.infer<typeof AgenticWritePayloadSchema>;

export const AgenticWriteStatusSchema = z.enum([
  "pending",
  // PRODUCT_DEV-564 Step 1: 承認ゲート対象 target 用の中間状態。人が承認すると
  // pending→approved になり、Writer が MODIFY(status=approved) を受けて実行する。
  "approved",
  // PRODUCT_DEV-564 レビュー H-2: 実行権の CAS claim 用。Writer は外部 API 呼び出し
  // 前に approved→executing を条件付き UpdateItem で確定し、Stream の at-least-once
  // 再配信による多重実行 (顧客への多重投稿/多重登録) を防ぐ。
  "executing",
  "executed",
  "rejected",
  "failed",
]);
export type AgenticWriteStatus = z.infer<typeof AgenticWriteStatusSchema>;

export const AgenticWriteRequesterSchema = z.enum([
  "strands",
  "cc-cli",
  "system",
  // PRODUCT_DEV-413: multi-agent orchestrator のサブシステム識別
  "plan-agent",
  "orchestrator",
]);
export type AgenticWriteRequester = z.infer<typeof AgenticWriteRequesterSchema>;

export const DDBAgenticWriteProposalSchema = z.object({
  PK: z.string(),
  SK: z.string(),
  proposalId: z.string(),
  jobId: z.string(),
  tenantId: z.string().nullable(),
  target: AgenticWriteTargetSchema,
  payload: AgenticWritePayloadSchema,
  requestedAt: z.string(),
  requestedBy: AgenticWriteRequesterSchema,
  status: AgenticWriteStatusSchema,
  result: z.string().optional(),
  executedAt: z.string().optional(),
  // PRODUCT_DEV-564 Step 1: 承認ゲート対象 target の承認時刻 (approve script が記録)。
  approvedAt: z.string().optional(),
  ttl: z.number().int().positive().optional(),
});
export type DDBAgenticWriteProposal = z.infer<
  typeof DDBAgenticWriteProposalSchema
>;

/*
 * JobTasks Table - PRODUCT_DEV-413 multi-agent orchestrator
 * PK: JOB#{jobId}  SK: TASK#{taskId}
 * GSI byStatus: PK=status, SK=createdAt
 * GSI byAgentRole: PK=agentRole, SK=createdAt (監査用)
 *
 * Plan Agent が initial-plan / replan で生成し、Orchestrator が依存解決後に
 * Worker (CC CLI EC2 or Strands Lambda) へ dispatch する。
 *
 * agentRole 一覧:
 *   - investigate-light: Strands Lambda で DDB/Backlog read-only 照会
 *   - investigate: CC CLI でコードベース調査・grep・file read
 *   - code-change: CC CLI worktree でファイル編集 + propose-github-pr
 *   - playwright-test: CC CLI + Playwright MCP で E2E 動作確認
 *   - refactor: code-change のサブセット (リファクタ専用)
 *   - finalize: Judge mode 出力を WriteProposal にまとめる
 */
export const AgentRoleSchema = z.enum(AGENT_ROLES);
export type AgentRole = z.infer<typeof AgentRoleSchema>;

export const JobTaskStatusSchema = z.enum([
  "pending",
  "ready",
  "running",
  "done",
  "failed",
  "skipped",
]);
export type JobTaskStatus = z.infer<typeof JobTaskStatusSchema>;

export const AssignedWorkerSchema = z.enum([
  "cc-cli-1",
  "cc-cli-2",
  "lambda-strands",
]);
export type AssignedWorker = z.infer<typeof AssignedWorkerSchema>;

export const DDBJobTaskSchema = z.object({
  PK: z.string(), // JOB#{jobId}
  SK: z.string(), // TASK#{taskId}
  jobId: z.string(),
  taskId: z.string(),
  iteration: z.number().int().nonnegative(),
  dependsOn: z.array(z.string()).default([]),
  agentRole: AgentRoleSchema,
  instructions: z.string().min(1).max(5000),
  expectedOutput: z.string().max(2000).optional(),
  status: JobTaskStatusSchema,
  assignedWorker: AssignedWorkerSchema.nullable().optional(),
  lockToken: z.string().nullable().optional(),
  lockExpiresAt: z.number().int().nonnegative().optional(),
  result: z.string().optional(),
  resultS3Key: z.string().optional(),
  proposalIds: z.array(z.string()).default([]),
  error: z.string().optional(),
  startedAt: z.number().int().nonnegative().optional(),
  completedAt: z.number().int().nonnegative().optional(),
  attemptCount: z.number().int().nonnegative().default(0),
  // タスク 1 件あたりの最大 attempt 回数 (orchestrator 側で enforce)。
  // 未設定なら DEFAULT_JOB_TASK_MAX_ATTEMPTS (= 10) を使う。
  // JobExpander が job.maxAttempts から伝播する。
  maxAttempts: z.number().int().positive().max(100).optional(),
  cost: z.number().nonnegative().default(0),
  // PRODUCT_DEV-413: per-task のモデル別トークン内訳 (job.modelUsage 集計の元データ)。
  modelUsage: AgenticJobModelUsageSchema.optional(),
  // PRODUCT_DEV-413 可観測性: タスク全トランスクリプト (stream-json / per-turn I/O) の
  // S3 key。jobs/{jobId}/task/{taskId}/transcript.jsonl。Admin の presigned URL 経由で
  // DAG ノード/トレース行から原因ログにドリルダウンする。未設定は「未取得」(後方互換)。
  transcriptS3Key: z.string().optional(),
  // PRODUCT_DEV-413 可観測性: タスク内 LLM 呼び出しの総レイテンシ (ms)。トレースの
  // タイミングバー/遅延強調に使う。未設定は「未計測」(後方互換)。
  latencyMs: z.number().int().nonnegative().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // GSI PK fields (denormalized for query efficiency)
  GSI_byStatus_PK: JobTaskStatusSchema.optional(),
  GSI_byStatus_SK: z.string().optional(),
  GSI_byAgentRole_PK: AgentRoleSchema.optional(),
  GSI_byAgentRole_SK: z.string().optional(),
});
export type DDBJobTask = z.infer<typeof DDBJobTaskSchema>;

// ============================================================
// JobEventLog (PRODUCT_DEV-413): マルチエージェント実行のフェーズ遷移ログ。
// プラン→ディスパッチ→処理Agent→評価→再計画 を時系列で残し、Admin の
// ジョブ詳細「実行ログ」タイムラインに表示する。各コンポーネントが best-effort で
// PutItem する (書き込み失敗してもジョブは止めない)。TTL 30日で自動 GC。
// ============================================================
export const JobEventLogPhaseSchema = z.enum([
  "plan", // Plan Agent: initial-plan 生成
  "dispatch", // Orchestrator: タスク dispatch
  "task-start", // Worker: タスク開始
  "tool", // Worker: tool 呼び出し (詳細粒度)
  "task-done", // Worker: タスク完了
  "task-failed", // Worker: タスク失敗
  "judge", // Plan Agent: judge 判定
  "iteration", // Orchestrator: iteration 完了
  "terminal", // Orchestrator/Plan Agent: ジョブ終端
]);
export type JobEventLogPhase = z.infer<typeof JobEventLogPhaseSchema>;

export const JobEventLogLevelSchema = z.enum(["info", "warn", "error"]);
export type JobEventLogLevel = z.infer<typeof JobEventLogLevelSchema>;

/*
 * PRODUCT_DEV-413 可観測性: イベント metadata の既知キー (ドリルダウン用)。
 * Admin のタイムライン/トレースが型安全に参照するための文書化スキーマ。
 * DDB の metadata フィールド自体は任意キーを許す record(unknown) のままにし、
 * 既存データ・将来の追加キーを壊さない (passthrough)。全フィールド optional。
 */
export const JobEventMetadataSchema = z
  .object({
    latencyMs: z.number().int().nonnegative().optional(), // LLM/tool 呼び出しのレイテンシ
    toolName: z.string().optional(), // tool フェーズ: 呼ばれた tool 名
    toolDurationMs: z.number().int().nonnegative().optional(),
    tokens: z
      .object({
        input: z.number().int().nonnegative().optional(),
        output: z.number().int().nonnegative().optional(),
        cacheRead: z.number().int().nonnegative().optional(),
      })
      .optional(),
    // transcript.jsonl 内の該当箇所への参照 (S3 key + 行/オフセット)。
    transcriptRef: z
      .object({
        s3Key: z.string(),
        line: z.number().int().nonnegative().optional(),
      })
      .optional(),
    // plan/judge フェーズ: Plan Agent 監査スナップショット (S3) への参照。
    auditS3Key: z.string().optional(),
  })
  .passthrough();
export type JobEventMetadata = z.infer<typeof JobEventMetadataSchema>;

export const DDBJobEventLogSchema = z.object({
  PK: z.string(), // JOB#{jobId}
  SK: z.string(), // EVT#{ISO timestamp}#{seq}
  jobId: z.string(),
  timestamp: z.string(), // ISO8601
  phase: JobEventLogPhaseSchema,
  actor: z.string(), // 例: plan-agent / orchestrator / strands / cc-cli-1
  message: z.string().max(2000),
  taskId: z.string().nullable().optional(),
  level: JobEventLogLevelSchema.default("info"),
  // 任意キーを許す (後方互換)。既知キーは JobEventMetadataSchema を参照。
  metadata: z.record(z.string(), z.unknown()).optional(),
  ttl: z.number().int().nonnegative(),
});
export type DDBJobEventLog = z.infer<typeof DDBJobEventLogSchema>;

// PRODUCT_DEV-413 可観測性: タスク DAG 取得 (GET /agentic-jobs/{jobId}/tasks)。
export const JobTaskListResponseSchema = z.object({
  tasks: z.array(DDBJobTaskSchema),
});
export type JobTaskListResponse = z.infer<typeof JobTaskListResponseSchema>;

// PRODUCT_DEV-413 可観測性: transcript / 監査スナップショットの presigned DL URL。
// expiresInSec は presign の有効秒数 (artifact DL 応答とも形を揃える)。
export const AgenticDownloadUrlResponseSchema = z.object({
  downloadUrl: z.string().url(),
  expiresInSec: z.number().int().positive().optional(),
});
export type AgenticDownloadUrlResponse = z.infer<
  typeof AgenticDownloadUrlResponseSchema
>;

// ============================================================
// Agentic 設定 (PRODUCT_DEV-413 可観測性): /ops/agentic/* SSM ガードレールを
// Admin から参照・編集する。書込は下記 AgenticSettingKeySchema の許可リストに
// 限定する (Rule5: 任意 SSM キーへの書込を防ぐ)。値は SSM ネイティブの文字列で
// やり取りし、type で UI 表現 (toggle/number/select) を切り替える。
// ============================================================
export const AgenticSettingTypeSchema = z.enum([
  "boolean",
  "number",
  "string",
  "enum",
]);
export type AgenticSettingType = z.infer<typeof AgenticSettingTypeSchema>;

// 編集可能な設定キー (bare 名。SSM では /ops/agentic/{key})。ここに無いキーは
// 参照のみ (editable=false)。PUT はこの enum でバリデートする。
export const AgenticSettingKeySchema = z.enum([
  "global-kill-switch",
  "read-only-mode",
  "self-loop-enabled",
  "max-parallel-workers",
  "max-iterations",
  "max-tasks-per-iteration",
  "max-total-tasks",
  "max-elapsed-hours",
  "task-hard-timeout-sec",
  "task-lock-timeout-seconds",
  "cost-limit-per-job",
  "cost-limit-per-iter",
  "cost-limit-daily-org",
  "pr-proposal-warn-loc",
  "pr-proposal-rate-per-24h",
  "max-open-ai-prs",
  "self-loop-max-retries",
  "self-loop-deploy-wait-sec",
  "self-loop-rollout-stage",
]);
export type AgenticSettingKey = z.infer<typeof AgenticSettingKeySchema>;

export const AgenticSettingSchema = z.object({
  key: z.string(), // bare 名 (例: max-parallel-workers)
  ssmName: z.string(), // 完全 SSM パス (/ops/agentic/...)
  label: z.string(),
  description: z.string(),
  category: z.string(), // グルーピング (安全装置 / 上限 / コスト / self-loop)
  type: AgenticSettingTypeSchema,
  value: z.string(), // 現在値 (SSM 生値)。未設定なら "" + editable 判定で表示
  editable: z.boolean(),
  options: z.array(z.string()).optional(), // type=enum のとき選択肢
});
export type AgenticSetting = z.infer<typeof AgenticSettingSchema>;

export const AgenticSettingChangeSchema = z.object({
  key: z.string(),
  at: z.string(), // ISO8601
  by: z.string(), // 変更者 (Admin ユーザ名/ID)
  from: z.string(),
  to: z.string(),
});
export type AgenticSettingChange = z.infer<typeof AgenticSettingChangeSchema>;

export const AgenticSettingsListResponseSchema = z.object({
  settings: z.array(AgenticSettingSchema),
  lastChange: AgenticSettingChangeSchema.nullable().optional(),
});
export type AgenticSettingsListResponse = z.infer<
  typeof AgenticSettingsListResponseSchema
>;

export const AgenticSettingUpdateRequestSchema = z.object({
  key: AgenticSettingKeySchema, // 許可リスト内のみ
  value: z.string().min(1).max(200),
});
export type AgenticSettingUpdateRequest = z.infer<
  typeof AgenticSettingUpdateRequestSchema
>;

export const AgenticSettingUpdateResponseSchema = z.object({
  setting: AgenticSettingSchema,
});
export type AgenticSettingUpdateResponse = z.infer<
  typeof AgenticSettingUpdateResponseSchema
>;

// ============================================================
// PRODUCT_DEV-841: Admin のコスト表示を 円/ドル で切り替えるための USD→JPY 為替レート。
// 実体は SSM `/{env}/admin/fx/usd-jpy-rate`(String, JSON 1 キー。名前は admin-metrics-stack.ts が決める)。
//
// **2 キーに分けない。** SSM に複数パラメータのトランザクション更新は無いので、
// rate を書いた直後に updated-at の Put が失敗すると「値は新しいのに日付は昨日」に
// なり、以後**毎アクセスで外部 API を叩き続ける**(「最頻で 1 日 1 回」が壊れる)。
// 1 キーなら片方だけ進む状態が構造的に起きない。
// 既存の `/ops/agentic/settings-last-change` / `requester-roles` も JSON 1 キー。
//
// 取得元は api.frankfurter.dev (ECB 公表参照レート・API キー不要)。**平日のみ更新**
// なので、土曜に叩くと金曜の日付が返る。更新の判定に rateDate(ECB 公表日) を使うと
// 週末は毎回叩きに行くことになるため、ゲートは syncedDate(自分が取得に成功した
// JST 暦日) を使う。判定の正本は apps/admin/api/lambda/fx-rate/_shared.ts。
// ============================================================

// 初期レートと範囲の正本は packages/shared-domain/cost/usd-jpy-rate.ts（PRODUCT_DEV-988）。
// Admin 以外（Jev の試し場・ハーネス）も同じ値を使うので共通部品へ移し、ここは同じ名前で
// 再 export する（値も名前も変えない＝既存の import はそのまま動く）。
import {
  USD_JPY_MAX_RATE,
  USD_JPY_MIN_RATE,
  USD_JPY_SEED_RATE,
} from "../cost/usd-jpy-rate";
export { USD_JPY_MAX_RATE, USD_JPY_MIN_RATE, USD_JPY_SEED_RATE };

export const FxRateSourceSchema = z.enum(["ecb", "seed"]);
export type FxRateSource = z.infer<typeof FxRateSourceSchema>;

/** SSM に保存する形 (正本)。 */
export const FxRateStateSchema = z.object({
  /** USD 1 に対する JPY。 */
  rate: z.number().positive().min(USD_JPY_MIN_RATE).max(USD_JPY_MAX_RATE),
  /** ECB 公表日 YYYY-MM-DD。シード値のときは ""。表示専用で更新判定には使わない。 */
  rateDate: z.string(),
  /** 最後に取得に**成功**した JST 暦日 YYYY-MM-DD。未同期は ""。更新判定のゲート。 */
  syncedDate: z.string(),
  /** 最後に取得を**試みた**時刻 (ISO8601)。未試行は ""。失敗時の再試行間隔に使う。 */
  attemptedAt: z.string(),
  source: FxRateSourceSchema,
  /** 直近の失敗理由 (短縮)。成功時は消す。 */
  lastError: z.string().max(200).optional(),
});
export type FxRateState = z.infer<typeof FxRateStateSchema>;

/** SSM が未作成/破損のときの初期状態。 */
export const USD_JPY_SEED_STATE: FxRateState = {
  rate: USD_JPY_SEED_RATE,
  rateDate: "",
  syncedDate: "",
  attemptedAt: "",
  source: "seed",
};

/**
 * 画面に返す形。base/quote を明示してあるのは、将来 JPY 以外を足したときに
 * 「何を何に換算したレートか」が呼び出し側から読めるようにするため。
 */
export const FxRateSchema = z.object({
  base: z.literal("USD"),
  quote: z.literal("JPY"),
  rate: z.number().positive(),
  /** ECB 公表日。"" ならまだ一度も取得できていない (= シード値)。 */
  rateDate: z.string(),
  syncedDate: z.string(),
  source: FxRateSourceSchema,
  /** true = 今日はまだ更新できていない (外部 API 失敗 or 未同期)。 */
  stale: z.boolean(),
});
export type FxRate = z.infer<typeof FxRateSchema>;

export const FxRateResponseSchema = z.object({ fxRate: FxRateSchema });
export type FxRateResponse = z.infer<typeof FxRateResponseSchema>;

// ============================================================
// PRODUCT_DEV-831: 梅子 (CC バッチ) を起動できる人の一覧。
// 実体は SSM `/ops/agentic/requester-roles`(String, JSON)。**1 行 = 1 人**で、
// 同じ人の Slack ID と Backlog ID が並ぶ。判定ロジックの正本は
// admin/agentic-requester.ts (Lambda 側と共用)。
// ============================================================
export const SLACK_USER_ID_PATTERN = /^[UW][A-Z0-9]{2,}$/;
export const BACKLOG_USER_ID_PATTERN = /^\d{1,19}$/;

export const AgenticRequesterMemberSchema = z
  .object({
    /** 表示用の氏名。権限判定には使わない。 */
    name: z.string().trim().min(1).max(100),
    /** Slack の user ID。この経路から起動しないなら空文字。 */
    slack: z.string().trim().max(64).default(""),
    /** Backlog の数値 user ID (文字列)。この経路から起動しないなら空文字。 */
    backlog: z.string().trim().max(64).default(""),
    tier: RequesterTierSchema,
  })
  .superRefine((m, ctx) => {
    // どちらの ID も無い行は、登録した本人は登録できたつもりでいるのに
    // 永久に起動できない。保存の時点で弾く。
    if (m.slack === "" && m.backlog === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Slack ID か Backlog ID のどちらかは必要です",
        path: ["slack"],
      });
    }
    if (m.slack !== "" && !SLACK_USER_ID_PATTERN.test(m.slack)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Slack ID の形式が違います (例: U08HGSB0RGW)",
        path: ["slack"],
      });
    }
    if (m.backlog !== "" && !BACKLOG_USER_ID_PATTERN.test(m.backlog)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Backlog ID は数字で指定してください (例: 2051503)",
        path: ["backlog"],
      });
    }
  });
export type AgenticRequesterMember = z.infer<typeof AgenticRequesterMemberSchema>;

export const AgenticRequesterListResponseSchema = z.object({
  members: z.array(AgenticRequesterMemberSchema),
  /** 保存済み JSON に不備があったときの警告 (壊れた行は除かれている)。 */
  warnings: z.array(z.string()),
  /** tier ごとに何ができるかの説明。画面に出して誤登録を減らす。 */
  tiers: z.array(
    z.object({
      tier: RequesterTierSchema,
      label: z.string(),
      description: z.string(),
      agentRoles: z.array(z.string()),
    }),
  ),
  /** 最後に更新した人と時刻 (権限変更の追跡用)。 */
  updatedBy: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type AgenticRequesterListResponse = z.infer<
  typeof AgenticRequesterListResponseSchema
>;

/*
 * 保存先の SSM Standard パラメータが 4KB までで、1 人あたり約 100 bytes。
 * **実際に入るのは約 40 人**なので、500 のような入らない上限を掲げない
 * (通る schema を通した先で AWS のエラーになると、画面に理由が出ない)。
 * 正確なバイト数は Admin の PUT が保存直前に測って断る。
 */
export const AGENTIC_REQUESTER_MEMBERS_MAX = 60;

export const AgenticRequesterUpdateRequestSchema = z.object({
  members: z.array(AgenticRequesterMemberSchema).max(AGENTIC_REQUESTER_MEMBERS_MAX),
});
export type AgenticRequesterUpdateRequest = z.infer<
  typeof AgenticRequesterUpdateRequestSchema
>;

export const JobEventLogListResponseSchema = z.object({
  events: z.array(DDBJobEventLogSchema),
});
export type JobEventLogListResponse = z.infer<
  typeof JobEventLogListResponseSchema
>;

/*
 * Plan Agent 出力スキーマ - PRODUCT_DEV-413
 * initial-plan / replan で Plan Agent (Lambda) が生成する DAG。
 * Orchestrator がこの schema 通過後にさらに Layer 3 validation
 * (cycle / depth / dependsOn 健全性 / Layer 11 内容妥当性) を実施する。
 */
export const PlanTaskNodeSchema = z.object({
  taskId: z
    .string()
    .min(1)
    .max(64)
    .regex(/^t-\d+-\d+$/), // 形式: t-<iteration>-<seq>
  agentRole: AgentRoleSchema,
  dependsOn: z.array(z.string()).max(20).default([]),
  instructions: z.string().min(1).max(5000),
  expectedOutput: z.string().max(2000).optional(),
});
export type PlanTaskNode = z.infer<typeof PlanTaskNodeSchema>;

export const TasksDagSchema = z.object({
  tasks: z.array(PlanTaskNodeSchema).min(1).max(20), // Layer 1: maxTasksPerIteration=20
});
export type TasksDag = z.infer<typeof TasksDagSchema>;

/*
 * Plan Agent Judge mode 出力 - PRODUCT_DEV-413
 * 全 iteration のタスク完了後、Plan Agent (judge mode) が以下のいずれかを判断:
 *   - complete: 依頼に対する成果が十分。最終 finalize task + WriteProposal で終了
 *   - replan: 次 iteration の DAG を生成して継続
 *   - fail: これ以上進めない。理由を残して終了
 */
export const JudgeDecisionSchema = z.enum(["complete", "replan", "fail"]);
export type JudgeDecision = z.infer<typeof JudgeDecisionSchema>;

export const PlanAgentJudgeOutputSchema = z.object({
  decision: JudgeDecisionSchema,
  reasoning: z.string().min(1).max(8000),
  nextTasks: TasksDagSchema.optional(),
});
export type PlanAgentJudgeOutput = z.infer<typeof PlanAgentJudgeOutputSchema>;

/*
 * Plan Agent invocation event - PRODUCT_DEV-413
 * Orchestrator → Plan Agent Lambda の invoke payload。
 */
export const PlanAgentInvocationSchema = z.object({
  jobId: z.string(),
  mode: z.enum(["initial-plan", "judge"]),
});
export type PlanAgentInvocation = z.infer<typeof PlanAgentInvocationSchema>;

/*
 * Orchestrator → Worker (SQS / Lambda invoke) message - PRODUCT_DEV-413
 */
export const WorkerTaskDispatchSchema = z.object({
  jobId: z.string(),
  taskId: z.string(),
  iteration: z.number().int().nonnegative(),
  agentRole: AgentRoleSchema,
  instructions: z.string().min(1).max(5000),
  expectedOutput: z.string().max(2000).optional(),
  lockToken: z.string(),
  lockExpiresAt: z.number().int().nonnegative(),
});
export type WorkerTaskDispatch = z.infer<typeof WorkerTaskDispatchSchema>;

/*
 * AgenticWorkflows Table - PRODUCT_DEV-413 Phase 2.x
 * 固定ワークフロー (jobKind="workflow") のテンプレート定義。
 *
 * Storage:
 *   PK: WORKFLOW#{workflowId}
 *   SK: v{N}      (各バージョンの履歴)
 *   SK: LATEST    (現行版のポインタ; version フィールドで実バージョンを参照)
 *
 * 更新時は TransactWriteItems で v{N+1} 追加 + LATEST 上書きを atomic に行う。
 * 削除は soft delete (deprecated=true) のみ許可。
 *
 * tasks[] は PlanTaskNodeSchema 互換 (= Plan Agent 出力と同じ shape) なので、
 * Plan Agent が生成した DAG をそのままワークフローとして保存する将来移行も可能。
 *
 * instructions / expectedOutput には Mustache 風変数 {{prompt}} {{backlogIssueKey}}
 * 等を埋め込み可能。実展開は JobExpander 側で workflow-template.ts を使う。
 * HTML エスケープ無効化記法 {{{raw}}} は禁止。
 */
export const WORKFLOW_TASKS_MAX_PER_WORKFLOW = 20;

export const WorkflowTaskTemplateSchema = PlanTaskNodeSchema;
export type WorkflowTaskTemplate = z.infer<typeof WorkflowTaskTemplateSchema>;

/*
 * PRODUCT_DEV-413 Phase B2: フォーマットテンプレート。
 * ワークフローに「空 Excel」テンプレを紐づけ、調査結果をそのフォーマットに埋め込む。
 * 説明 Markdown はアップロードせず、WF 登録後に非同期で自動生成する
 * (空 Excel の構造を読んで Bedrock が生成)。
 *   - instructionStatus: pending(生成待ち) | ready(生成完了) | failed(生成失敗)
 *   - templateS3Key: ops bucket の workflow-formats/{workflowId}/template.xlsx
 *   - instructionMarkdown: 生成された「各セルに何を入れるか」の説明 (ready 時のみ)
 */
export const WorkflowFormatStatusSchema = z.enum([
  "pending",
  "ready",
  "failed",
]);
export type WorkflowFormatStatus = z.infer<typeof WorkflowFormatStatusSchema>;

export const WorkflowFormatSchema = z.object({
  templateS3Key: z.string().min(1),
  instructionMarkdown: z.string().optional(),
  instructionStatus: WorkflowFormatStatusSchema,
  instructionError: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type WorkflowFormat = z.infer<typeof WorkflowFormatSchema>;

export const DDBWorkflowSchema = z.object({
  PK: z.string(), // WORKFLOW#{workflowId}
  SK: z.string(), // "LATEST" or "v{N}"
  workflowId: z.string().min(1).max(64),
  version: z.number().int().positive(),
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
  tasks: z
    .array(WorkflowTaskTemplateSchema)
    .min(1)
    .max(WORKFLOW_TASKS_MAX_PER_WORKFLOW),
  deprecated: z.boolean().default(false),
  // PRODUCT_DEV-413 Phase B2: 紐づく Excel フォーマットテンプレ (optional / 後方互換)。
  format: WorkflowFormatSchema.optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DDBWorkflow = z.infer<typeof DDBWorkflowSchema>;

/*
 * Workflows CRUD - PRODUCT_DEV-413 Phase 2.x
 * Admin API endpoints (apps/admin/api/lambda/workflows/) の入出力スキーマ。
 */
export const CreateWorkflowSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
  tasks: z
    .array(WorkflowTaskTemplateSchema)
    .min(1)
    .max(WORKFLOW_TASKS_MAX_PER_WORKFLOW),
});
export type CreateWorkflow = z.infer<typeof CreateWorkflowSchema>;

export const UpdateWorkflowSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
  tasks: z
    .array(WorkflowTaskTemplateSchema)
    .min(1)
    .max(WORKFLOW_TASKS_MAX_PER_WORKFLOW),
});
export type UpdateWorkflow = z.infer<typeof UpdateWorkflowSchema>;

export const WorkflowListResponseSchema = z.object({
  workflows: z.array(DDBWorkflowSchema),
});
export type WorkflowListResponse = z.infer<typeof WorkflowListResponseSchema>;

/*
 * PRODUCT_DEV-413 Phase B2: フォーマットテンプレ (空 Excel) アップロード用
 * presigned PUT URL を発行するエンドポイントのレスポンス。
 * クライアントは uploadUrl に xlsx を PUT 後、confirm エンドポイントを叩く。
 */
export const WorkflowFormatUploadUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  templateS3Key: z.string(),
  expiresInSec: z.number().int().positive(),
});
export type WorkflowFormatUploadUrlResponse = z.infer<
  typeof WorkflowFormatUploadUrlResponseSchema
>;

/*
 * PRODUCT_DEV-493: 編集ログ計測（Admin 編集ログ ダッシュボード）
 * unizo の UnizoEditEvents（hot, 7日TTL）を admin の日次 aggregator が集計し、
 * TieredRollupStore で daily/weekly/monthly を S3 へ永続化する。
 * 下記は S3 に保存する集計（RollupEnvelope.aggregate）と取得 API の形。
 */
export interface EditMetricsFieldEdit {
  label?: string;
  count: number;
}
export interface EditMetricsPresetAggregate {
  presetId: string;
  presetName?: string;
  registered: number; // 仮→本の遷移総数（register_transition）
  withEdit: number; // うち編集ありで本登録
  withoutEdit: number; // うち無編集で本登録
  revisionRate: number; // withEdit / registered（registered=0 のとき 0）
  fieldEdits: Record<string, EditMetricsFieldEdit>; // fieldId → 編集回数
}
export interface EditMetricsTotals {
  registered: number;
  withEdit: number;
  withoutEdit: number;
  fieldEditCount: number;
}
/** 1テナント分の集計（perPreset/totals は全テナント版と同形）。 */
export interface EditMetricsTenantAggregate {
  tenantId: string;
  tenantName?: string;
  perPreset: Record<string, EditMetricsPresetAggregate>;
  totals: EditMetricsTotals;
}
export interface EditMetricsAggregate {
  // 全テナント横断ビュー（後方互換: 既存フィールドを維持）
  perPreset: Record<string, EditMetricsPresetAggregate>;
  totals: EditMetricsTotals;
  // PRODUCT_DEV-493: テナント別内訳（tenantId → 集計）。旧データには無いため optional。
  perTenant?: Record<string, EditMetricsTenantAggregate>;
}
export type EditMetricsGranularity = "daily" | "weekly" | "monthly";

/** GET /api/edit-metrics のレスポンス */
export interface EditMetricsResponse {
  granularity: EditMetricsGranularity;
  period: string | null; // 要求 period（未指定時は最新）
  aggregate: EditMetricsAggregate | null;
  index: {
    daily: string[];
    weekly: string[];
    monthly: string[];
  };
  // PRODUCT_DEV-493: テナント絞り込み用の選択肢（aggregate.perTenant 由来）。
  tenants?: { tenantId: string; tenantName?: string }[];
}

/*
 * PRODUCT_DEV-493: 編集 History（誰が・いつ・どの商談で・どの項目を・何から何に直したか）
 * unizo UnizoEditEvents（7日TTL hot）の field_edit イベントを 1 編集 = 1 行で返す。
 * 「保存（ピン留め）」した行は admin 所有 EditEventArchive へ複製して無期限保持し、
 * 解除すると ttl=now+7日 を付与して 7 日後に失効させる（集計には載らない詳細を時限保持）。
 */
export interface EditHistoryChange {
  fieldId: string;
  label?: string; // 項目表示名（解決できなければ undefined → UI は fieldId 表示）
  before: string | null; // 文字列化した変更前値（null=未設定）
  after: string | null; // 文字列化した変更後値
}
export interface EditHistoryRow {
  // 一意キー（unizo EditEvent の PK/SK）。ピン留め操作・行の de-dup に使う。
  eventPk: string;
  eventSk: string;
  tenantId: string;
  tenantName?: string;
  meetingId: string;
  meetingTitle?: string; // best-effort（UnizoMeetings GSI1 から解決、無ければ undefined）
  presetId: string;
  actorUserId: string;
  actorName?: string; // 表示名（USERS_TABLE から解決、無ければ undefined）
  createdAt: string; // ISO8601（編集日時）
  changes: EditHistoryChange[];
  pinned: boolean; // 保存中（archive に ttl 無しで存在）
}
/** GET /api/edit-history のレスポンス（行は createdAt 降順）。 */
export interface EditHistoryResponse {
  rows: EditHistoryRow[];
  windowDays: number; // 取得対象の直近日数（既定 7）
  truncated: boolean; // 上限超過で打ち切ったか（UI に注意表示）
  // 絞り込みセレクト用の選択肢（rows から導出）。
  tenants: { tenantId: string; tenantName?: string }[];
  meetings: { meetingId: string; meetingTitle?: string }[];
  actors: { actorUserId: string; actorName?: string }[];
}
/**
 * POST /api/edit-history/{pin,unpin} の入力（対象イベントの PK/SK + 対象テナント）。
 * tenantId ではなく targetTenantId を使う: Admin の useApi が body/query の `tenantId` を
 * 必ず currentTenantId(UMEEE) で上書きするため（feedback_admin_useapi_tenantid_override）。
 */
export const EditHistoryPinRequestSchema = z.object({
  eventPk: z.string().min(1),
  eventSk: z.string().min(1),
  targetTenantId: z.string().min(1), // archive PK の組立に使う正準 tenantId
});
export type EditHistoryPinRequest = z.infer<typeof EditHistoryPinRequestSchema>;
/** POST /api/edit-history/{pin,unpin} のレスポンス。 */
export interface EditHistoryPinResponse {
  ok: boolean;
  pinned: boolean; // 操作後の保存状態（pin→true / unpin→false）
}

// ============================================================
// GateKeeper Playground (PRODUCT_DEV-476)
// 文字起こしから本物の項目整理(Pass1) + チェックLLM(Pass2)を実行し、
// 結果を保存・一覧・差分確認するチューニングツール用のスキーマ。
// ============================================================

/** モデルパラメータ（per-run 上書き）。reasoning 系では temperature/top_p は実行時に自動除去される。 */
export const GatekeeperPlaygroundModelParamsSchema = z.object({
  temperature: z.number().optional(),
  top_p: z.number().optional(),
  max_completion_tokens: z.number().int().positive().optional(),
  reasoning_effort: z.enum(["minimal", "low", "medium", "high"]).optional(),
});
export type GatekeeperPlaygroundModelParams = z.infer<
  typeof GatekeeperPlaygroundModelParamsSchema
>;

/** プロンプトの出所。override=画面で編集した本文、effective=現行 ONP/デフォルト */
export const GatekeeperPlaygroundPromptSourceSchema = z.enum([
  "override",
  "effective",
]);
export type GatekeeperPlaygroundPromptSource = z.infer<
  typeof GatekeeperPlaygroundPromptSourceSchema
>;

/**
 * PRODUCT_DEV-589: 商談URLシミュレーションで実際に使った文字起こしの出所。
 *
 * 本番の conversationLog はリクエスト body 経由で DB に残らないため、保存済みログから復元する。
 * dialogueLog（AI+User 全文・対話モードのみ）が最も本番に近い。どれを使ったかは結果の
 * 比較可能性に直結するため run に記録して UI に表示する。
 */
export const GatekeeperPlaygroundLogSourceSchema = z.enum([
  "dialogueLog",
  "cleanedTranscript",
  "rawTranscript",
  // PRODUCT_DEV-873: 議事録URLモード。議事録（要約・決定・次のアクション・全文）から本番と同じ関数
  // （buildMinutesMeetingLog）で組んだ対話ログ
  "minutes",
]);
export type GatekeeperPlaygroundLogSource = z.infer<
  typeof GatekeeperPlaygroundLogSourceSchema
>;

/** 実行ステータス。pending=実行中(非同期 worker 処理中) */
export const GatekeeperPlaygroundRunStatusSchema = z.enum([
  "pending",
  "completed",
  "failed",
]);
export type GatekeeperPlaygroundRunStatus = z.infer<
  typeof GatekeeperPlaygroundRunStatusSchema
>;

/**
 * PRODUCT_DEV-589: 「UI比較」ビュー用に、抽出データ 1 件を preset の表示順で整形した 1 列ぶんの行。
 *
 * admin web は unizo preset の fields（順序/ラベル/サブフィールド）を取得できないため、
 * 整形は unizo 側（`buildFieldDisplay`）で行い、admin は整形済みの行だけを受け取って描画する
 * （プロダクト境界を越えた preset 内部構造の import を避ける）。列（商談データ/固定/選択中）の
 * 合成と差分ハイライトは web 側の責務。
 */
export const FieldDisplayRowSchema = z.object({
  id: z.string(), // フィールドID（title 行は "title"）
  title: z.string(), // 表示名（フィールドラベル）
  body: z.string(), // 本文（未設定は空文字）
  subFields: z.array(z.object({ label: z.string(), value: z.string() })),
});
export type FieldDisplayRow = z.infer<typeof FieldDisplayRowSchema>;

/**
 * 実行結果レコード（DDB 保存形）。
 * 実行は非同期（POST で pending 作成 → worker が unizo を呼んで完了/失敗で更新）。
 * 出力系・使用プロンプト・解決モデルは completed になって初めて確定するため optional。
 */
export const DDBGatekeeperPlaygroundRunSchema = z.object({
  PK: z.string(), // RUN#{runId}
  SK: z.string(), // RUN (固定値)
  runId: z.string(),
  // 入力
  // PRODUCT_DEV-589: meetingId モードでは実行時に商談から復元した本文を書き戻す
  // （履歴の入力エコー・差分ビューがこのフィールドを参照するため）。pending 中は空文字。
  transcription: z.string(),
  /** 商談URLシミュレーションの対象商談。未設定 = 手貼り transcript モード */
  meetingId: z.string().optional(),
  /** 復元に使った保存済みログの種別（meetingId / minutesId モード） */
  logSource: GatekeeperPlaygroundLogSourceSchema.optional(),
  /** PRODUCT_DEV-873: 議事録URLモードの対象議事録（ULID）。meetingId とは排他 */
  minutesId: z.string().optional(),
  /** PRODUCT_DEV-873: 議事録のタイトル（履歴表示用スナップショット。completed で確定） */
  minutesTitle: z.string().optional(),
  /** PRODUCT_DEV-873: 本番との差の注記（例: 商談日を議事録の作成日で入れた） */
  minutesNote: z.string().optional(),
  tenantId: z.string(), // preset/辞書を解決した対象テナント
  presetId: z.string().nullable(), // null = preset なし(レガシーフィールドガイド)
  presetName: z.string().nullable().optional(),
  // 実行設定(入力)
  modelRequested: z.string().optional(), // 操作者の選択。未指定=本番同等(テナントフラグ)
  modelParams: GatekeeperPlaygroundModelParamsSchema.optional(),
  completionPromptOverride: z.string().optional(),
  itemCheckPromptOverride: z.string().optional(),
  /** PRODUCT_DEV-873: 議事録用の箇条書きルール（MEETING_FIELD_EXTRACTION_BULLET_STYLE）の上書き。議事録モードのみ */
  bulletStylePromptOverride: z.string().optional(),
  // 実行結果(completed で確定)
  model: z.string().optional(), // 実際に使用したモデル
  modelFromTenantFlag: z.boolean().optional(), // true=テナントフラグで解決(本番同等)
  completionPromptUsed: z.string().optional(), // Pass1 使用プロンプト全文
  itemCheckPromptUsed: z.string().optional(), // Pass2 使用プロンプト全文
  /** PRODUCT_DEV-873: 議事録モードで Pass1 に連結した箇条書きルールの全文 */
  bulletStylePromptUsed: z.string().optional(),
  promptSource: GatekeeperPlaygroundPromptSourceSchema.optional(),
  // 出力(柔軟な JSON)
  pass1Data: z.record(z.unknown()).optional(),
  pass1Confidence: z.record(z.number()).optional(),
  pass2Corrections: z.record(z.unknown()).optional(),
  pass2AppliedFields: z.array(z.string()).optional(),
  itemCheckApplied: z.number().int().optional(),
  pass1DurationMs: z.number().optional(),
  pass2DurationMs: z.number().optional(),
  itemCheckStatus: z.enum(["applied", "no-change", "failed"]).optional(),
  supplementedData: z.record(z.unknown()).optional(),
  supplementedFields: z.array(z.string()).optional(),
  confidence: z.record(z.number()).optional(),
  // PRODUCT_DEV-589: 「UI比較」ビュー用の preset 順整形（meetingId モードのみ）。
  // fieldDisplay=この実行の結果 / currentFieldDisplay=商談の保存データ（左列アンカー）。
  fieldDisplay: z.array(FieldDisplayRowSchema).optional(),
  currentFieldDisplay: z.array(FieldDisplayRowSchema).optional(),
  // 状態
  status: GatekeeperPlaygroundRunStatusSchema,
  error: z.string().optional(),
  saved: z.boolean(), // true = TTL 無制限(保存)
  ttl: z.number().optional(), // epoch 秒。saved 時は属性ごと削除
  createdBy: z.string(),
  createdAt: z.string(), // ISO 8601
  updatedAt: z.string(), // ISO 8601
});
export type DDBGatekeeperPlaygroundRun = z.infer<
  typeof DDBGatekeeperPlaygroundRunSchema
>;

/**
 * 商談ID（6文字の [a-z0-9]。`utils/string.ts` の randomStr(6)）。Playground の商談URLモードと、
 * 振り返りプレビューの材料（PRODUCT_DEV-872）の入力で使う。
 */
export const MEETING_ID_PATTERN = /^[a-z0-9]{6}$/;

/** PRODUCT_DEV-873: 議事録ID（ULID: 26文字の Crockford base32。I・L・O・U を含まない） */
export const MINUTES_ID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;

/**
 * POST /api/gatekeeper-playground/runs の入力。model 未指定=本番同等(テナントフラグで解決)。
 * 対象テナントは targetTenantId で渡す（useApiPost が body.tenantId を currentTenantId で
 * 上書きするため、名前を分ける＝PRODUCT_DEV-494 と同じ回避）。
 */
export const CreateGatekeeperPlaygroundRunSchema = z
  .object({
    // PRODUCT_DEV-589: meetingId 指定時は商談から復元するため transcription は不要。
    // 手貼りモードでは従来どおり必須（下の refine で担保）。
    transcription: z.string().max(100000).optional(),
    /** 商談URLシミュレーション（本番同等の再現）。商談ID = 6文字の [a-z0-9] */
    meetingId: z
      .string()
      .regex(MEETING_ID_PATTERN, "商談IDは6文字の英小文字・数字です")
      .optional(),
    /**
     * PRODUCT_DEV-873: 議事録URLモード。議事録から本番と同じ形で対話ログを組み、画面で選んだ
     * preset で生成する（商談は作らない）。議事録ID = ULID（26文字の Crockford base32）。meetingId と排他
     */
    minutesId: z
      .string()
      .regex(MINUTES_ID_PATTERN, "議事録IDは26文字の英大文字・数字です")
      .optional(),
    /** PRODUCT_DEV-873: 議事録用の箇条書きルールの上書き（議事録モードのみ有効） */
    bulletStylePromptOverride: z.string().max(20000).optional(),
    targetTenantId: z.string().min(1),
    presetId: z.string().nullable().optional(),
    presetName: z.string().nullable().optional(), // 表示用スナップショット
    model: z.string().optional(),
    modelParams: GatekeeperPlaygroundModelParamsSchema.optional(),
    completionPromptOverride: z.string().optional(),
    itemCheckPromptOverride: z.string().optional(),
  })
  .refine(
    (v) =>
      (v.meetingId != null && v.meetingId !== "") ||
      (v.minutesId != null && v.minutesId !== "") ||
      (v.transcription != null && v.transcription.trim() !== ""),
    { message: "transcription・meetingId・minutesId のいずれかが必要です" },
  )
  .refine((v) => !(v.meetingId && v.minutesId), {
    message: "meetingId と minutesId は同時に指定できません",
  });
export type CreateGatekeeperPlaygroundRun = z.infer<
  typeof CreateGatekeeperPlaygroundRunSchema
>;

/** PATCH /api/gatekeeper-playground/runs/{runId} の入力（保存/解除） */
export const UpdateGatekeeperPlaygroundRunSchema = z.object({
  saved: z.boolean(),
});
export type UpdateGatekeeperPlaygroundRun = z.infer<
  typeof UpdateGatekeeperPlaygroundRunSchema
>;

export const GatekeeperPlaygroundRunListResponseSchema = z.object({
  runs: z.array(DDBGatekeeperPlaygroundRunSchema),
});
export type GatekeeperPlaygroundRunListResponse = z.infer<
  typeof GatekeeperPlaygroundRunListResponseSchema
>;

/**
 * unizo playground 実行 Lambda の戻り値（admin worker が直接 Invoke で受け取る wire 契約）。
 * これが unizo↔admin 間の **単一の正本**。unizo handler はこの型で戻り値を組み立て、
 * admin worker は受信時に safeParse で検証する（プロダクト境界を越えた型 import を避けつつ、
 * 片側の変更による暗黙のドリフトを実行時に確実に検知する）。
 */
export const GatekeeperPlaygroundCompletionTraceSchema = z.object({
  pass1Data: z.record(z.unknown()),
  pass1Confidence: z.record(z.number()),
  pass2Corrections: z.record(z.unknown()),
  pass2AppliedFields: z.array(z.string()),
  itemCheckApplied: z.number(),
  pass1DurationMs: z.number(),
  pass2DurationMs: z.number(),
  itemCheckStatus: z.enum(["applied", "no-change", "failed"]),
});

export const GatekeeperPlaygroundCompletionOutputSchema = z.object({
  result: z.object({
    supplementedData: z.record(z.unknown()).optional(),
    supplementedFields: z.array(z.string()).optional(),
    confidence: z.record(z.number()).optional(),
  }),
  trace: GatekeeperPlaygroundCompletionTraceSchema.nullable(),
  completionPromptUsed: z.string(),
  itemCheckPromptUsed: z.string(),
  promptSource: GatekeeperPlaygroundPromptSourceSchema,
  model: z.string(),
  modelFromTenantFlag: z.boolean(),
  // PRODUCT_DEV-589: meetingId（商談URL）モードのみ。手貼り transcript モードでは未設定。
  /**
   * 商談から復元して実際に GK へ渡した文字起こし（admin が run.transcription へ書き戻す）。
   * 手貼り経路の transcription と同じ属性へ入るため上限も揃える（DDB の 1 item 400KB 回避）。
   */
  resolvedTranscription: z.string().max(100000).optional(),
  /** 上記がどの保存済みログ由来か（比較可能性の判断材料として UI に出す） */
  logSource: GatekeeperPlaygroundLogSourceSchema.optional(),
  /** 商談の presetId（画面の preset 選択ではなく商談の値を使ったことを示す） */
  resolvedPresetId: z.string().nullable().optional(),
  /** 商談の preset 名（履歴表示用スナップショット。未解決時は null） */
  resolvedPresetName: z.string().nullable().optional(),
  // PRODUCT_DEV-589: 「UI比較」ビュー用の preset 順整形（meetingId かつ preset 解決時のみ）。
  /** この実行の結果（supplementedData）を preset 順に整形したもの（右列/固定列の素材） */
  fieldDisplay: z.array(FieldDisplayRowSchema).optional(),
  /** 商談に保存済みの extractedData を preset 順に整形したもの（左列アンカー） */
  currentFieldDisplay: z.array(FieldDisplayRowSchema).optional(),
  // PRODUCT_DEV-873: 議事録URLモードのみ
  /** 議事録のタイトル（履歴表示用） */
  minutesTitle: z.string().optional(),
  /** Pass1 に連結した箇条書きルールの全文（上書き or 既定） */
  bulletStylePromptUsed: z.string().optional(),
  /** 本番との差の注記（例: 商談日を議事録の作成日で入れた） */
  minutesNote: z.string().optional(),
});
export type GatekeeperPlaygroundCompletionOutput = z.infer<
  typeof GatekeeperPlaygroundCompletionOutputSchema
>;

// ============================================================================
// 振り返りプレビューの材料（PRODUCT_DEV-872）
//   Admin の ONP 設定「振り返り」のプレビューで、商談URLを指定して実録音と同じ全文を見る。
//   admin Lambda が unizo の Lambda を同期 invoke して材料を受け取り、そのまま画面へ返す。
//   **どこにも保存しない**（個人情報を含むので、ログにも本文を出さない）。
// ============================================================================

/**
 * 商談が対象テナントに無い（存在しない・他テナントの ID・ゴミ箱）ときに、unizo の Lambda が投げる
 * エラーの名前。直接 Invoke の応答では `errorType` に入るので、admin はこれで「見つからない」
 * （404）と、それ以外の失敗（502）を見分ける。
 */
export const MINUTES_REVIEW_PREVIEW_MEETING_NOT_FOUND = "MinutesReviewPreviewMeetingNotFound";

/**
 * POST /api/minutes-review-preview/source の入力。
 * 対象テナントは targetTenantId で渡す（useApiPost が body.tenantId を currentTenantId で
 * 上書きするため。Playground と同じ）。
 */
export const MinutesReviewPreviewSourceRequestSchema = z.object({
  targetTenantId: z.string().min(1),
  meetingId: z
    .string()
    .regex(MEETING_ID_PATTERN, "商談IDは6文字の英小文字・数字です"),
});
export type MinutesReviewPreviewSourceRequest = z.infer<
  typeof MinutesReviewPreviewSourceRequestSchema
>;

/** プレビューに要るプリセットの部分（振り返りのオーバーレイと聞く観点の材料）。 */
export const MinutesReviewPreviewPresetSchema = z.object({
  presetId: z.string(),
  name: z.string(),
  /** 削除済みか。削除済みでも、その商談のプリセットなら実録音と同じく返す（選択肢には注記する） */
  isDelete: z.boolean().optional(),
  /** 項目（`$ref` のままのことがある。解決は画面側＝実録音と同じ関数） */
  fields: z.array(z.record(z.unknown())).optional(),
  /** プリセットの追加観点 */
  reviewPerspectives: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
        question: z.string(),
        linkedFieldId: z.string().optional(),
      }),
    )
    .optional(),
  /** 動的コンテキストの設定（辞書の入・切を見る） */
  dynamicContextSettings: z
    .object({
      enableCompanySearch: z.boolean().optional(),
      enableIndividualSearch: z.boolean().optional(),
      enableDictionary: z.boolean().optional(),
    })
    .optional(),
});
export type MinutesReviewPreviewPreset = z.infer<
  typeof MinutesReviewPreviewPresetSchema
>;

/** その商談に紐づく議事録（振り返りの起点の候補）。 */
export const MinutesReviewPreviewMinutesSchema = z.object({
  minutesId: z.string(),
  title: z.string(),
  date: z.string(),
  summary: z.string().optional(),
  decisions: z.array(z.string()).optional(),
  /** 次のアクションの本文（実録音が渡すのと同じく text だけ） */
  nextActions: z.array(z.string()).optional(),
  /** 関連企業の名前（議事録から始めた振り返りの事前確定の法人名）。企業の表から引けたときだけ */
  companyName: z.string().optional(),
});
export type MinutesReviewPreviewMinutes = z.infer<
  typeof MinutesReviewPreviewMinutesSchema
>;

/** テナント辞書の1件（有効なものだけ・表示順）。 */
export const MinutesReviewPreviewDictionaryEntrySchema = z.object({
  word: z.string(),
  reading: z.string(),
  readings: z.array(z.string()).optional(),
  formalName: z.string().optional(),
  meaning: z.string().optional(),
  category: GlobalDictionaryCategorySchema,
});

/**
 * unizo の振り返りプレビュー材料 Lambda の戻り値（admin Lambda が直接 Invoke で受け取る wire 契約）。
 * unizo の handler はこの型で戻り値を組み、admin は受信時に safeParse で検証してから画面へ返す。
 */
export const MinutesReviewPreviewSourceSchema = z.object({
  meeting: z.object({
    meetingId: z.string(),
    /** 商談のプリセット（無ければ null） */
    presetId: z.string().nullable(),
    /** 商談の取引先名（商談詳細から始めた振り返りの事前確定の法人名）。無ければ null */
    company: z.string().nullable(),
    /** 商談の保存値。値の形は項目ごとに違う（文字列・配列・オブジェクト） */
    extractedData: z.record(z.unknown()),
    /** 商談の状態。"processing" の間は、実際には振り返りを始められない */
    status: z.string(),
  }),
  /** テナントのプリセット（下書きを除く。削除済みは、その商談のプリセットだけ残す） */
  presets: z.array(MinutesReviewPreviewPresetSchema),
  /** 紐づく議事録（ゴミ箱を除く・新しい順） */
  minutes: z.array(MinutesReviewPreviewMinutesSchema),
  /** テナント辞書（有効なもの・表示順） */
  dictionary: z.array(MinutesReviewPreviewDictionaryEntrySchema),
});
export type MinutesReviewPreviewSource = z.infer<
  typeof MinutesReviewPreviewSourceSchema
>;

/**
 * PRODUCT_DEV-506: 音声メモ / ToDo 利用統計（admin）。
 * UnizoMemos / UnizoTodos を日次 Scan→集計し、TieredRollupStore で daily/weekly/monthly を S3 に永続化。
 * EditMetrics と同じ三段ロールアップ基盤を流用する。
 */
/** メモ/ToDo の機能別カウント。todoCompletionRate は todoDone/todoTotal（total=0 のとき 0）。
 *  完了率は「期間内に作成された ToDo のうち done のもの」の作成コホート基準（createdAt 基準）。
 *  ※ completedAt 基準（期間内に完了した数）は別メトリクスであり本集計では扱わない。 */
export interface MemoUsageFunctionCounts {
  memoTotal: number;
  memoVoice: number;
  memoManual: number;
  todoTotal: number;
  todoDone: number;
  todoPending: number;
  todoCompletionRate: number;
}
/** 単日（YYYY-MM-DD, JST）の機能別カウント。Recharts の時系列グラフ用。 */
export interface MemoUsageDayPoint extends MemoUsageFunctionCounts {
  date: string;
}
/** 1ユーザー分の集計。 */
export interface MemoUsageUserAggregate {
  userId: string;
  userName?: string;
  counts: MemoUsageFunctionCounts;
  // YYYY-MM-DD(JST) → その日のカウント。GET が envelope.parts から再構成して付与する（S3 保存形には含めない）。
  // 全体線に重ねるユーザー単体の時系列グラフ用。旧データ/未付与時は undefined。
  series?: Record<string, MemoUsageDayPoint>;
}
/** 1テナント分の集計（perUser 内包）。 */
export interface MemoUsageTenantAggregate {
  tenantId: string;
  tenantName?: string;
  counts: MemoUsageFunctionCounts;
  perUser: Record<string, MemoUsageUserAggregate>;
  // YYYY-MM-DD(JST) → その日のカウント。GET が envelope.parts から再構成して付与する（S3 保存形には含めない）。
  // 全体線に重ねるテナント単体の時系列グラフ用。旧データ/未付与時は undefined。
  series?: Record<string, MemoUsageDayPoint>;
}
/** S3 に保存する集計（RollupEnvelope.aggregate）。 */
export interface MemoUsageAggregate {
  totals: MemoUsageFunctionCounts;
  // YYYY-MM-DD(JST) → その日のカウント。merge で日付キーを和集合加算 → 週次/月次に日次点が蓄積。
  series: Record<string, MemoUsageDayPoint>;
  // tenantId → テナント別内訳。旧データには無いため optional。
  perTenant?: Record<string, MemoUsageTenantAggregate>;
}
// "range": 任意期間（直近N日 / カスタム from-to）。月次ロールアップ内の日次 series を結合し全体集計のみ返す。
export type MemoUsageGranularity = "daily" | "weekly" | "monthly" | "range";

/** GET /api/memo-usage のレスポンス（EditMetricsResponse と同形）。 */
export interface MemoUsageResponse {
  granularity: MemoUsageGranularity;
  period: string | null;
  aggregate: MemoUsageAggregate | null;
  index: {
    daily: string[];
    weekly: string[];
    monthly: string[];
  };
  tenants?: { tenantId: string; tenantName?: string }[];
}

// ============================================================================
// PRODUCT_DEV-653: 開発量の可観測性（誰が・どの事業に・どれだけ開発投資したか）
// ----------------------------------------------------------------------------
// AutopilotSessions / JobTasks（agentic ジョブの実績）と、ops が収集する
// Claude Code のテレメトリを日次で突き合わせ、TieredRollupStore で
// metrics バケットの `dev-metrics/` prefix へ daily/weekly/monthly を永続化する。
// edit-metrics / memo-usage と同じ三段ロールアップ基盤を流用する。
//
// ⚠ 設計上の約束が 3 つある。崩すと集計が静かに壊れる:
//   1. 葉はすべて**加算のみ**。Math.max / 平均 / 「最初に見た値」を入れない
//      （TieredRollupStore の merge は可換・結合的でなければならない）
//   2. **比率をここに持たせない**。分子分母だけ持ち、除算は get / 画面側で行う
//      （memo-usage は todoCompletionRate を持たせて merge 後に再計算しているが、
//        フィールドが増えるほど再計算漏れが起きるため今回は持たない方針）
//   3. 非数値メタは「集合の和集合」か「論理 OR」しか許さない
// ============================================================================

/** 金額の意味。**この 2 つを 1 つの数字に潰してはいけない**（PRODUCT_DEV-653）。 */
export type DevCostBasis =
  /** agentic ジョブの Bedrock 従量。実際に請求される金額（Cost Explorer と突合できる）。 */
  | "bedrock-actual"
  /** 対話 Claude Code。個人 Max は定額なので**定価換算の名目値**であって実支出ではない。 */
  | "max-notional";

/** モデル別のトークン内訳。cacheWrite は 1.25 倍課金なので必ず保持する。 */
export interface DevTokenCounts {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
}

/** 加算のみで構成された計測値。すべて可換・結合的に畳み込める。 */
export interface DevMetricsCounts {
  /** Bedrock 実費（agentic ジョブ）。 */
  billedCostUsd: number;
  /** 定価換算の名目値（対話分）。 */
  nominalCostUsd: number;
  /** 正規化済みモデルキー → トークン内訳。換算根拠として必ず残す。 */
  tokensByModel: Record<string, DevTokenCounts>;
  /** 価格表に無いモデルのトークン合計。0 でなければ画面に警告を出す。 */
  unknownModelTokens: number;
  jobCount: number;
  taskCount: number;
  sessionCount: number;
  /** 協業度の分子分母（比率にはしない → 上記の約束 2）。 */
  turns: number;
  humanInterventions: number;
  /**
   * 人間の介入を**実際に観測できた**作業単位の数。
   *
   * agentic ジョブ側は現時点で介入回数を取れない（human-handoff / 手動 resume を
   * 拾えていない）ため humanInterventions が常に 0 になる。これをそのまま
   * `1 - 0/turns` に入れると**自律度が常に 100% という嘘**になるので、
   * 「測れた単位が 1 つも無ければ自律度を出さない」ための分母を別に持つ。
   */
  interventionMeasuredUnits: number;
  toolCalls: number;
  toolRejections: number;
  retries: number;
  activeSeconds: number;
  linesAdded: number;
  linesRemoved: number;
}

/** 単日（YYYY-MM-DD, JST）の計測値。recharts の時系列グラフ用。 */
export interface DevMetricsDayPoint extends DevMetricsCounts {
  date: string;
}

/** 発生源（どの環境から回した Claude Code か）。 */
export interface DevMetricsSourceAggregate {
  /** `<installId>:<origin>`。管理画面で名前を付けるときのキー。 */
  sourceKey: string;
  /** 登録済みなら付けた名前、未登録なら `<installId 先頭8桁> (<origin>)`。 */
  label: string;
  /** true = 管理画面で名前を付けるべきもの。**「不明」に丸めない**。 */
  unnamed: boolean;
  counts: DevMetricsCounts;
  /** GET が envelope.parts から再構成して付与する（S3 保存形には含めない）。 */
  series?: Record<string, DevMetricsDayPoint>;
}

/** 事業（product）別の内訳。 */
export interface DevMetricsProductAggregate {
  /** `.claude/products/INDEX.md` の product（Backlog の product 軸カテゴリと 1:1）。 */
  product: string;
  /** dev-lab/Tasks のようなサブプロダクト。無ければ undefined。 */
  subProduct?: string;
  counts: DevMetricsCounts;
  series?: Record<string, DevMetricsDayPoint>;
}

/** 未帰属の理由。**黙って捨てず内訳まで数える**（合計を全体と一致させるため）。 */
export interface DevMetricsUnattributedReasons {
  /** 課題キーが取れなかった。 */
  noIssueKey: number;
  /** 複数課題にまたがるセッション（按分は根拠が無いので per-issue から除外）。 */
  multiIssue: number;
  /** 課題キーはあるが Backlog 索引に無い。 */
  notInIndex: number;
  /** 課題はあるが product 軸カテゴリが未設定。 */
  noCategory: number;
  /** カテゴリはあるが未知の product 名。 */
  unknownProduct: number;
}

/** S3 に保存する集計（RollupEnvelope.aggregate）。 */
export interface DevMetricsAggregate {
  totals: DevMetricsCounts;
  /** YYYY-MM-DD(JST) → その日の計測値。merge で日付キーを和集合加算する。 */
  series: Record<string, DevMetricsDayPoint>;
  /** `product` または `product/subProduct` → 内訳。 */
  perProduct: Record<string, DevMetricsProductAggregate>;
  /** sourceKey → 内訳。 */
  perSource: Record<string, DevMetricsSourceAggregate>;
  /** userId → 内訳。 */
  perUser: Record<string, DevMetricsUserAggregate>;
  /** 事業に帰属できなかった分。**perProduct + unattributed の合計 == totals** が不変条件。 */
  unattributed: {
    counts: DevMetricsCounts;
    reasons: DevMetricsUnattributedReasons;
  };
  /** 使った価格表のバージョン集合（merge = 和集合）。監査用。 */
  pricebookVersions: string[];
  /** Backlog 索引が 24h より古い状態で集計したか（merge = 論理 OR）。 */
  staleIndex: boolean;
  /**
   * テレメトリが 1 件でも存在した日数（加算）。
   * 「対話 0 件」と「対話が未計測」を区別するために持つ。
   */
  telemetryDaysObserved: number;
  /**
   * PRODUCT_DEV-967: Jev のおすすめ（AskUserQuestion の観測）。
   * 観測が 1 件も無い日・Jev 導入前の保存済み集計には**キー自体が無い**（「0 件」と区別する）。
   * merge は加算（可換・結合的）。両方に無ければキーを作らない。
   */
  jevDecisions?: JevDecisionAggregate;
}

/** ユーザ別の内訳。 */
export interface DevMetricsUserAggregate {
  userId: string;
  userName?: string;
  counts: DevMetricsCounts;
  series?: Record<string, DevMetricsDayPoint>;
}

/**
 * PRODUCT_DEV-967: Jev の確信度の帯（正本）。境界は下側を含む（0.95 ちょうどは "0.95-0.99"）。
 *
 * ⚠ **帯の変更・追加は破壊的変更。** 帯ごとの件数は S3 の集計（dev-metrics/ の
 * daily/weekly/monthly）に永続化されているので、変えると過去の集計と合算できなくなる。
 * 読み手は欠けた帯を 0 件として扱う（古い集計で落ちないようにするため）。
 */
export const JEV_BANDS = ["0.90-0.95", "0.95-0.99", "0.99+"] as const;
export type JevBandKey = (typeof JEV_BANDS)[number];

/** 帯ごとの件数。 */
export interface JevBandCounts {
  recommended: number;
  kept: number;
}

/**
 * PRODUCT_DEV-967: AskUserQuestion の観測の件数。**比率は持たない**（分子分母だけ。除算は画面）。
 *   おすすめ = jevStatus "decided" のみ / 採用 = decided かつ kept のみ。
 */
export interface JevDecisionCounts {
  /** 観測した問いの総数（excluded を含む）。 */
  observed: number;
  /** 顧客名などで Jev に送らなかった問い。 */
  excluded: number;
  /** Jev に聞いたが確信度 0.9 未満だった問い。 */
  uncertain: number;
  /** Jev が応答しなかった問い。 */
  unavailable: number;
  /** おすすめ（jevStatus "decided"）。 */
  recommended: number;
  /** おすすめのうち人が同じ選択肢を選んだ数。 */
  kept: number;
  /** 確信度の帯ごとの内訳。確信度が読めない decided はどの帯にも入らない。 */
  bands: Record<JevBandKey, JevBandCounts>;
}

/** ユーザ別の Jev 観測。 */
export interface JevUserAggregate {
  userId: string;
  userName?: string;
  counts: JevDecisionCounts;
}

/** Jev 観測の集計（DevMetricsAggregate.jevDecisions）。 */
export interface JevDecisionAggregate {
  /** 全員の合計。 */
  totals: JevDecisionCounts;
  /** userId → 内訳。 */
  perUser: Record<string, JevUserAggregate>;
  /** 観測が 1 件以上あった日数（加算）。0 / キー無しなら画面は「データなし」。 */
  daysObserved: number;
}

/** "range": 任意期間。月次ロールアップ内の日次 series を結合し全体集計のみ返す。 */
export type DevMetricsGranularity = "daily" | "weekly" | "monthly" | "range";

/** カテゴリ未設定の課題（画面の「未分類」タブで潰す対象）。 */
export interface DevMetricsUncategorizedIssue {
  issueKey: string;
  summary: string;
  statusName: string;
  assigneeName?: string;
  updated: string;
  /** 投資額があるのに未分類なもの。運用上こちらを先に潰す。 */
  billedCostUsd: number;
}

/** GET /api/dev-metrics のレスポンス（MemoUsageResponse と同形）。 */
export interface DevMetricsResponse {
  granularity: DevMetricsGranularity;
  period: string | null;
  aggregate: DevMetricsAggregate | null;
  index: {
    daily: string[];
    weekly: string[];
    monthly: string[];
  };
  /** 未分類課題の一覧（別 prefix の索引から読む。無ければ空）。 */
  uncategorizedIssues?: DevMetricsUncategorizedIssue[];
  /** 索引の生成時刻（鮮度の表示用）。 */
  indexGeneratedAt?: string;
}

// ============================================================================
// PRODUCT_DEV-413: Shortcuts ページの CodePipeline 進捗ゲージ
// ============================================================================
/**
 * CodePipeline 1 ステージの状態。`status` は CodePipeline の StageExecutionStatus
 * （Succeeded / InProgress / Failed / Stopped / Stopping / Cancelled）。未実行は "NotRun"。
 * 将来値が増えても壊れないよう string で受け、UI 側で既知値だけ色付けする。
 */
export interface PipelineStageState {
  name: string;
  status: string;
}

/** 承認待ちの stage/action。承認/却下 API のリクエストにそのまま渡す。 */
export interface PipelineApprovalTarget {
  stageName: string;
  actionName: string;
}

/** 1 パイプラインの状態。`found=false` は存在しない / 権限なしで取得不可。 */
export interface PipelineStatus {
  name: string;
  found: boolean;
  stages: PipelineStageState[];
  /**
   * PRODUCT_DEV-628: 手動承認（ManualApproval, actionName "ApproveInfra"）が応答待ち
   * （InProgress かつ token あり）のとき、その stage/action を示す。undefined は承認待ちなし。
   * 承認トークンはセキュリティ上ここには含めない（承認 API 側でサーバが実行時に都度取得する）。
   */
  pendingApproval?: PipelineApprovalTarget;
}

/** GET /api/shortcuts/pipeline-status のレスポンス。 */
export interface PipelineStatusListResponse {
  pipelines: PipelineStatus[];
  /**
   * PRODUCT_DEV-742: この env で承認の対象になるテナントキー。
   *
   * **画面にテナント名を書かない**ため、選択肢はサーバから受け取る。
   * `dev/tools/check-no-customer-names.sh` が製品コード全体を検査しており、
   * 顧客名をフロントに直書きできない。正本は `infra/tenants`。
   */
  tenants: string[];
  /**
   * PRODUCT_DEV-742: テナント×env×product の公開 URL。`{tenant: {env: {product: url}}}`。
   *
   * **画面で URL を組み立てないため。** 以前は `${env}.unizo.umeee-app.com` を
   * 直書きしていて、引っ越し後の形（`unizo.stg...`）にもテナントにも追随せず、
   * 無印ともちぎんに同じ（どちらでもない）URL を出していた。
   * 正本は `infra/tenants` の `hostNameFor` で、synth 時に解決した値が流れてくる。
   *
   * **キーが無い＝その組み合わせには配っていない**（例: とちぎんの survey、dev-lab の stg）。
   */
  productUrls?: Record<string, Record<string, Record<string, string>>>;
}

// ----------------------------------------------------------------------------
// PRODUCT_DEV-628: Admin から CodePipeline リリース承認（AWS コンソール不要）
// ----------------------------------------------------------------------------
/**
 * CodePipeline リリース承認を許可する specialFlag。
 * このフラグを持つ admin だけが Admin から承認/却下できる（agenticUser と同じ運用）。
 */
export const PIPELINE_APPROVER_FLAG = SPECIAL_FLAGS.pipelineApprover.key;

// CodePipeline 命名規則（英数 . _ -）。pipeline/stage/action 名の想定集合。
const PIPELINE_TOKEN_RE = /^[A-Za-z0-9._-]{1,100}$/;

/** POST /api/shortcuts/pipeline-approval のリクエスト（承認/却下）。 */
export const PipelineApprovalRequestSchema = z.object({
  /**
   * PRODUCT_DEV-742: どのテナントの pipeline か。**省略時は無印。**
   *
   * ⑧ で pipeline が deploy アカウントへ出るため、承認の宛先はテナントごとに違う。
   * 既定を無印にしてあるのは、テナントを送らない古い画面から呼ばれても
   * 従来と同じ相手になるようにするため。
   */
  tenant: z.string().regex(PIPELINE_TOKEN_RE).optional(),
  pipelineName: z.string().regex(PIPELINE_TOKEN_RE),
  stageName: z.string().regex(PIPELINE_TOKEN_RE),
  actionName: z.string().regex(PIPELINE_TOKEN_RE),
  result: z.enum(["Approved", "Rejected"]),
});
export type PipelineApprovalRequest = z.infer<
  typeof PipelineApprovalRequestSchema
>;

/** POST /api/shortcuts/pipeline-approval のレスポンス。 */
export interface PipelineApprovalResponse {
  tenant: string;
  pipelineName: string;
  stageName: string;
  actionName: string;
  result: "Approved" | "Rejected";
}

// ==========================================
// 問い合わせ管理 (PRODUCT_DEV-635)
// ==========================================
// データ実体は unizo 所有 UnizoInquiries（DDBInquiry は @shared-domain/unizo/contract）。
// admin は全テナント横断で一覧/詳細を読み、AI ドラフトの lazy read-back・担当アサイン・
// 指示付き再生成・回答承認（+ unizo 側 in-app 通知作成）を行う。
// 対象テナント/ユーザは admin useApi の tenantId 上書きと衝突しないよう
// targetTenantId / targetUserId の別名で受ける。

/** admin へ返す問い合わせ view（内部キー PK/SK のみ落とす。draftAnswer は admin には見せる）。 */
export type AdminInquiryView = Omit<
  import("../unizo/contract").DDBInquiry,
  "PK" | "SK"
>;

export interface AdminInquiriesResponse {
  inquiries: AdminInquiryView[];
}

export interface AdminInquiryResponse {
  inquiry: AdminInquiryView;
}

/** 対象問い合わせの特定子（PK 復元用）。 */
const AdminInquiryTargetSchema = z.object({
  targetTenantId: z.string().min(1),
  targetUserId: z.string().min(1),
});

/**
 * POST /api/inquiries/{inquiryId}/answer — 回答・返信。
 * PRODUCT_DEV-908: 1回きりではなくなった。まだ answer が無ければ answer に、あれば messages に
 * 運営の返信として足す（どちらも status=answered。closed にも返信できる）。
 * やり取りが INQUIRY_MAX_THREAD_LENGTH 通に達していたら 409（code=INQUIRY_THREAD_FULL。unizo の追記と同じ）。
 */
export const AdminInquiryAnswerRequestSchema = AdminInquiryTargetSchema.extend({
  text: z.string().min(1).max(4000),
  references: z
    .array(
      z.object({
        // PRODUCT_DEV-908: "faq" は articleId=faqId、"page" は articleId=常設ページの id。未指定はガイド記事（従来どおり）。
        // 関連リンクは3つまで（INQUIRY_LINKS_MAX。以前は5つ）
        kind: InquiryAnswerReferenceKindEnum.optional(),
        categoryId: z.string().max(64),
        articleId: z.string().max(64),
        title: z.string().max(200),
      }),
    )
    .max(INQUIRY_LINKS_MAX)
    .default([]),
  // PD-660: 回答に添えるイラスト。未指定は done（★を掲げるゾウ）
  illustration: InquiryAnswerIllustrationEnum.default("done"),
  // PRODUCT_DEV-908: 画面で見ていたやり取りの通数（inquiryThreadLength）。違えば 409 CONFLICT
  // （ほかの運営が先に答えた・利用者が追記した、を見落として送らせない）。未指定は確かめない（古い画面）
  expectedThreadLength: z.number().int().min(1).optional(),
});
export type AdminInquiryAnswerRequest = z.infer<
  typeof AdminInquiryAnswerRequestSchema
>;

/** POST /api/inquiries/{inquiryId}/assign — 担当アサイン（自分に付ける。引き継ぎ確認は web ダイアログ）。 */
export const AdminInquiryAssignRequestSchema = AdminInquiryTargetSchema;
export type AdminInquiryAssignRequest = z.infer<
  typeof AdminInquiryAssignRequestSchema
>;

/** POST /api/inquiries/{inquiryId}/regenerate — AI ドラフトの指示付き再生成。 */
export const AdminInquiryRegenerateRequestSchema =
  AdminInquiryTargetSchema.extend({
    instruction: z.string().max(2000).optional(),
  });
export type AdminInquiryRegenerateRequest = z.infer<
  typeof AdminInquiryRegenerateRequestSchema
>;

/** POST /api/inquiries/{inquiryId}/close — 運営が閉じる（closedBy=operator。PRODUCT_DEV-908）。 */
export const AdminInquiryCloseRequestSchema = AdminInquiryTargetSchema;
export type AdminInquiryCloseRequest = z.infer<
  typeof AdminInquiryCloseRequestSchema
>;

/** POST /api/inquiries/{inquiryId}/reopen — 閉じた問い合わせを open に戻す（PRODUCT_DEV-908）。 */
export const AdminInquiryReopenRequestSchema = AdminInquiryTargetSchema;
export type AdminInquiryReopenRequest = z.infer<
  typeof AdminInquiryReopenRequestSchema
>;

// ---- よくある質問（FAQ）の管理（PRODUCT_DEV-908）----
// データ実体は unizo 所有 UnizoInquiryFaqs（DDBInquiryFaq は @shared-domain/unizo/contract）。
// 全テナント共通の1セットで、運営 Umeee だけが admin で登録・編集する。

/** admin へ返す FAQ（内部キー PK/SK だけ落とす。下書きも含む）。 */
export type AdminInquiryFaqView = Omit<
  import("../unizo/contract").DDBInquiryFaq,
  "PK" | "SK"
>;

/** GET /api/inquiry-faqs — 全件（下書きを含む・sortOrder 昇順）。 */
export interface AdminInquiryFaqsResponse {
  faqs: AdminInquiryFaqView[];
}

/** POST / PUT /api/inquiry-faqs[/{faqId}] の応答。 */
export interface AdminInquiryFaqResponse {
  faq: AdminInquiryFaqView;
}

/** POST /api/inquiry-faqs — 作成。sortOrder 未指定は末尾（今の最大 + 1）。 */
/** PRODUCT_DEV-908: 関連リンク1件（運営の回答と FAQ で共通）。title はサーバが正本の名前で上書きする */
const AdminInquiryLinkSchema = z.object({
  kind: InquiryAnswerReferenceKindEnum.optional(),
  categoryId: z.string().max(64),
  articleId: z.string().max(64),
  title: z.string().max(200),
});

export const AdminInquiryFaqCreateRequestSchema = z.object({
  question: z.string().trim().min(1).max(INQUIRY_FAQ_QUESTION_MAX_LENGTH),
  answer: z.string().trim().min(1).max(INQUIRY_FAQ_ANSWER_MAX_LENGTH),
  category: InquiryCategorySchema,
  published: z.boolean(),
  // 「この回答を FAQ に登録」から作ったときの元の問い合わせ
  sourceInquiryId: z.string().min(1).max(64).optional(),
  sortOrder: z.number().int().min(0).max(1_000_000).optional(),
  // PRODUCT_DEV-908: 関連リンク（ガイドの記事・ほかの FAQ・常設ページ）。3つまで
  links: z.array(AdminInquiryLinkSchema).max(INQUIRY_LINKS_MAX).optional(),
  // PRODUCT_DEV-908: sourceInquiryId 付きを公開するときは必須（個人名・社名などを消したと運営が確かめた印）。
  // FAQ は全テナント共通なので、1テナントの利用者の文が他テナントに出るのを防ぐ
  sanitizedConfirmed: z.boolean().optional(),
});
export type AdminInquiryFaqCreateRequest = z.infer<
  typeof AdminInquiryFaqCreateRequestSchema
>;

/** PUT /api/inquiry-faqs/{faqId} — 部分更新（並べ替えも sortOrder でここから）。 */
export const AdminInquiryFaqUpdateRequestSchema = z
  .object({
    question: z.string().trim().min(1).max(INQUIRY_FAQ_QUESTION_MAX_LENGTH),
    answer: z.string().trim().min(1).max(INQUIRY_FAQ_ANSWER_MAX_LENGTH),
    category: InquiryCategorySchema,
    published: z.boolean(),
    sortOrder: z.number().int().min(0).max(1_000_000),
    // PRODUCT_DEV-908: 関連リンク（空配列で全部外す）
    links: z.array(AdminInquiryLinkSchema).max(INQUIRY_LINKS_MAX),
    // PRODUCT_DEV-908: sourceInquiryId 付きの FAQ を公開に切り替えるときは必須（作成時と同じ）
    sanitizedConfirmed: z.boolean(),
  })
  .partial()
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: "no fields to update",
  });
export type AdminInquiryFaqUpdateRequest = z.infer<
  typeof AdminInquiryFaqUpdateRequestSchema
>;

// ---------------------------------------------------------------------------
// コスト画面 (PRODUCT_DEV-724)
// ---------------------------------------------------------------------------
// AWS / GitHub / OpenAI の利用料金を日次・週次・月次で見る。
//
// データは **ops の日次コスト通知が朝 1 回保存したもの**を読むだけ。admin 側で
// 取得し直さないのは、Slack に出た数字と画面の数字を必ず一致させるため。
//
// ⚠ **残高は扱わない。** OpenAI の残高は API で取れず (credit_grants は 403 =
// ブラウザ専用)、auto-recharge のため人が入れた値もすぐ陳腐化する。残高は
// 各サービスの課金ページへのリンクから見てもらう。

export const CostGranularitySchema = z.enum(["daily", "weekly", "monthly"]);
export type CostGranularity = z.infer<typeof CostGranularitySchema>;

/** 推移グラフの 1 点。period は daily=YYYY-MM-DD / weekly=GGGG-Www / monthly=YYYY-MM。 */
export const CostSeriesPointSchema = z.object({
  period: z.string(),
  awsUsd: z.number(),
  githubUsd: z.number(),
  openaiUsd: z.number(),
  totalUsd: z.number(),
});
export type CostSeriesPoint = z.infer<typeof CostSeriesPointSchema>;

/**
 * カード用の当月サマリは **ops が S3 に保存した payload そのもの**を返す。
 *
 * ⚠ ここで部分集合を再宣言しない。Slack の文面も同じ payload から作られており、
 * admin が別途「たぶんこう」と写した型を持つと、ops が形を変えたときに
 * TypeScript が検知できないまま画面だけ壊れる。
 * 正本は `packages/shared-domain/cost/contract.ts`。
 */
import { CostLatestSnapshotSchema } from "../cost/contract";
export { CostLatestSnapshotSchema, parseCostLatest } from "../cost/contract";
export type { CostLatestSnapshot } from "../cost/contract";

/** GET /api/cost-metrics のレスポンス。画面が要るものを 1 回で返す。 */
export const CostMetricsResponseSchema = z.object({
  /** 直近実行時のサマリ。まだ 1 度も保存されていなければ null。 */
  latest: CostLatestSnapshotSchema.nullable(),
  daily: z.array(CostSeriesPointSchema),
  weekly: z.array(CostSeriesPointSchema),
  monthly: z.array(CostSeriesPointSchema),
});
export type CostMetricsResponse = z.infer<typeof CostMetricsResponseSchema>;

/* ------------------------------------------------------------------ *
 * PRODUCT_DEV-760: テナントモニタリング 一覧・詳細（Umeee 社内の CS 向け）
 *
 * 「どのテナントに手を入れるべきか」を一覧で一次判断し、詳細で中身を見る。
 * スコアの算出仕様は apps/admin/api/lib/tenantHealth/score.ts が正本
 * （閾値・重み・引き下げ規則をコードとテストで固定してある）。
 * ------------------------------------------------------------------ */

/** 総合評価の区分。pending は導入30日未満で、まだ評価しない状態（一覧では「集計中」）。 */
export const TenantHealthBandSchema = z.enum([
  "critical",
  "warning",
  "healthy",
  "pending",
]);
export type TenantHealthBand = z.infer<typeof TenantHealthBandSchema>;

/** 一覧の 1 行。 */
export const TenantHealthRowSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  /** 総合評価 0-100。band === "pending" のときは null。 */
  score: z.number().nullable(),
  band: TenantHealthBandSchema,
  /** 未対応の問い合わせ件数。 */
  openInquiryCount: z.number(),
  /** 最終アクティブ日（"YYYY-MM-DD"）。一度もなければ null。 */
  lastActiveDate: z.string().nullable(),
  /** 最終アクティブからの経過日数。一度もなければ null。 */
  daysSinceLastActive: z.number().nullable(),
  /**
   * このテナントだけ集計に失敗したか。true のとき数値は当てにならない。
   * 1 件の失敗で一覧全体を落とさず、失敗したことを画面に出すためのフラグ。
   */
  aggregationFailed: z.boolean(),
});
export type TenantHealthRow = z.infer<typeof TenantHealthRowSchema>;

/** GET /api/tenant-health のレスポンス。評価が低い順に並べて返す。 */
export const TenantHealthListResponseSchema = z.object({
  tenants: z.array(TenantHealthRowSchema),
  /** 集計した時点（JST の日付）。 */
  asOf: z.string(),
  /**
   * PRODUCT_DEV-947: **アクティブ率・総合評価の 30 日窓に、記録が欠けていた期間が
   * まだ入っているか。** true のあいだは画面に断りを出す。
   *
   * 数字そのものは動かしていない（窓を掛けると点数が下がる一方のため）。
   * `engagementCleanFrom` を過ぎれば false になり、断りは自然に消える。
   */
  engagementIncomplete: z.boolean().optional(),
  /** 30 日窓が欠けた期間を含まなくなる日（"YYYY-MM-DD"）。 */
  engagementCleanFrom: z.string().optional(),
});
export type TenantHealthListResponse = z.infer<
  typeof TenantHealthListResponseSchema
>;

/** 詳細画面のアカウント別ログイン状況（1 行）。 */
export const TenantAccountUsageStatSchema = z.object({
  userId: z.string(),
  userName: z.string(),
  /**
   * 窓のうち、この人が使った日数。
   *
   * ⚠ **名前に 30 と入っているが、窓は 30 日固定ではない**（PRODUCT_DEV-947）。
   * 実際の窓は同じ応答の `usageWindowDays` / `usageWindowSince` を見ること。
   * 名前を変えないのは、この応答を読む側を壊さないため（RULES Rule3）。
   */
  usedDays30d: z.number(),
  /**
   * `usedDays30d ÷ usageWindowDays` を百分率にしたもの（小数なし）。
   *
   * ⚠ **分母は 30 固定ではない**（PRODUCT_DEV-947）。記録が使えるのが 10 日ぶんしか
   * 無ければ 10 で割る。固定の 30 で割ると、記録が無かった日を「使わなかった日」として
   * 数えることになり、実態より低く出る。
   * ⚠ **同じ行の `recordCount30d` は本物の 30 日窓**（別のテーブルから数えており、
   * ログインの記録が無かった期間にも商談記録は残っている）。**1 行に窓が 2 種類ある。**
   */
  usedRate30d: z.number(),
  lastUsedDate: z.string().nullable(),
  /** 直近 30 日にこの人が登録した商談記録の件数（お試しを除く）。 */
  recordCount30d: z.number(),
  /** テナント全体の登録件数に占める割合（%・小数なし）。偏りを見るための値。 */
  recordShare30d: z.number(),
});
export type TenantAccountUsageStat = z.infer<
  typeof TenantAccountUsageStatSchema
>;

/** 詳細画面の週次トレンド（1 本）。 */
export const TenantWeeklyRecordBucketSchema = z.object({
  from: z.string(),
  to: z.string(),
  label: z.string(),
  count: z.number(),
  isCurrent: z.boolean(),
});
export type TenantWeeklyRecordBucket = z.infer<
  typeof TenantWeeklyRecordBucketSchema
>;

/** 詳細画面の問い合わせ 1 件（一覧の再掲。本文は問い合わせ詳細画面で見る）。 */
export const TenantHealthInquirySchema = z.object({
  inquiryId: z.string(),
  /** 問い合わせ詳細（/inquiries/{id}?t=&u=）へのリンクを組むのに要る。 */
  tenantId: z.string(),
  userId: z.string(),
  title: z.string(),
  status: z.string(),
  createdAt: z.string(),
  assigneeName: z.string().nullable(),
});
export type TenantHealthInquiry = z.infer<typeof TenantHealthInquirySchema>;

/** GET /api/tenant-health/{tenantId} のレスポンス。 */
export const TenantHealthDetailResponseSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  score: z.number().nullable(),
  band: TenantHealthBandSchema,
  /** 内訳（pending でも算出する）。 */
  engagementScore: z.number(),
  activityScore: z.number(),
  /** 未対応の問い合わせにより band を引き下げたか。 */
  escalatedByInquiry: z.boolean(),

  accountCount: z.number(),
  activeAccounts7d: z.number(),
  activeAccounts30d: z.number(),
  /** 直近7日/30日のアクティブ率（百分率・小数なし）。 */
  activeRate7d: z.number(),
  activeRate30d: z.number(),
  /**
   * PRODUCT_DEV-947: **アカウント別の利用率**（`accounts[].usedRate30d`）を出すのに
   * 使った窓。通常は 30 日だが、ログインの記録が使えるのが 2026-09-08 以降なので
   * それまでは短い。
   *
   * ⚠ **上の `activeRate7d` / `activeRate30d` には掛かっていない。**
   * アクティブ人数は窓を狭めると実在する記録を捨てるだけで、点数が下がる方向にしか
   * 動かないため（`apps/admin/api/lib/tenantHealth/window.ts` の表）。
   *
   * 旧版の API から受けたときに undefined になりうるので、**画面は既定値を置くこと**。
   */
  usageWindowDays: z.number().optional(),
  /** 上の窓が実際に始まる日（"YYYY-MM-DD"）。画面の断りに出す。 */
  usageWindowSince: z.string().optional(),
  /**
   * 窓が 30 日に満たないか。**true のあいだは画面に断りを出す。**
   * 窓が短いほど率は高めに出るので、黙って並べると「30 日で 27%」と読まれる。
   */
  usageWindowTruncated: z.boolean().optional(),
  /** なぜ窓が短いか。`record` は記録の開始、`tenant` は導入日、`none` は切られていない。 */
  usageWindowReason: z.enum(["record", "tenant", "none"]).optional(),
  /**
   * PRODUCT_DEV-947: **アクティブ率・総合評価の 30 日窓に、記録が欠けていた期間が
   * まだ入っているか。** true のあいだは画面に断りを出す。
   *
   * 数字そのものは動かしていない（窓を掛けると点数が下がる一方のため）。
   * `engagementCleanFrom` を過ぎれば false になり、断りは自然に消える。
   */
  engagementIncomplete: z.boolean().optional(),
  /** 30 日窓が欠けた期間を含まなくなる日（"YYYY-MM-DD"）。 */
  engagementCleanFrom: z.string().optional(),
  lastActiveDate: z.string().nullable(),
  daysSinceLastActive: z.number().nullable(),
  /** 継続日数（テナントの誰かが使った日が途切れずに続いている日数）。 */
  streakDays: z.number(),
  /** テナント作成からの経過日数（「集計中」の根拠）。 */
  /** 利用開始からの日数。テナント作成日が記録されていなければ null。 */
  daysSinceOnboarding: z.number().nullable(),

  /** 直近30日の商談記録数と、面談した顧客の社数。 */
  recordCount30d: z.number(),
  companyCount30d: z.number(),
  /** 前30日（31〜60日前）の商談記録数。前月比の分母。 */
  recordCountPrev30d: z.number(),
  /**
   * 直近30日の**お試し商談**の件数（実績には数えていない分）。
   * お試しばかりで本番記録が無いテナントに気づくために別枠で出す。
   */
  testRecordCount30d: z.number(),

  /**
   * アカウント別の利用状況。登録件数の多い順で、**有効なアカウントを全件返す**。
   *
   * ⚠ 以前は上位 5 件で打ち切っていたが、**未利用の人は必ず最下位に来るため
   * 確実に隠れる**という致命的な欠陥があった。手作業のモニタリングでは
   * 「12 名中 3 名が未利用」が最重要の観測点だったので、打ち切りをやめた。
   * 件数が極端に多いテナントに備えて `ACCOUNT_ROWS_HARD_LIMIT` だけ残してある。
   */
  accounts: z.array(TenantAccountUsageStatSchema),
  /** 上限に達して返せなかったアカウント数。通常は 0。 */
  accountsOmittedCount: z.number(),
  /**
   * 一度も利用していないアカウント数（accounts 全体に対する数）。
   *
   * ⚠ **`usedDays30d`（可変の窓）と `recordCount30d`（30 日固定）の AND**
   * （PRODUCT_DEV-947）。**1 つの条件に窓が 2 種類混ざる。**
   * 窓が短いあいだは「その期間に 1 度も開かず、30 日 1 件も記録していない人」に
   * なるので、**未利用を多めに数える**。窓が 30 日に戻れば従来と同じ意味へ戻る。
   */
  unusedAccountCount: z.number(),
  weeklyTrend: z.array(TenantWeeklyRecordBucketSchema),
  inquiries: z.array(TenantHealthInquirySchema),

  asOf: z.string(),
});
export type TenantHealthDetailResponse = z.infer<
  typeof TenantHealthDetailResponseSchema
>;

// ============================================================
// PRODUCT_DEV-1088 商談記録の人の採点（Admin）
// ============================================================
// 人が商談記録を採点し、AI の点数（Unizo の UnizoMeetingScores）と比べる。
// 決定（2026-10-14）: 採点は1種類。保存も訂正も履歴に足していき、比べるのは人ごとの最新。
// AI の点・ほかの人の採点は最初から見せる。訂正で段階を変えた観点には理由が要る。
// A（聞いたことに応えた）・B（振り返り）は数えて決まるので人は付けない（表示だけ）。

/** 根拠の文・理由の上限（1観点ぶん） */
export const MEETING_HUMAN_SCORE_TEXT_MAX = 2000;

/** 人が付けた1観点 */
export const MeetingHumanScoreAspectSchema = z.object({
  level: MeetingScoreLevelSchema,
  /** 根拠にした記録の文（画面で記録の文を押して入れる）。無ければ空文字 */
  evidence: z.string().max(MEETING_HUMAN_SCORE_TEXT_MAX),
  /** 前の採点から段階を変えた理由（訂正で段階を変えたときは必須） */
  reason: z.string().max(MEETING_HUMAN_SCORE_TEXT_MAX).optional(),
});
export type MeetingHumanScoreAspect = z.infer<typeof MeetingHumanScoreAspectSchema>;

export const MeetingHumanScoreAspectsSchema = z.object({
  issueDepth: MeetingHumanScoreAspectSchema,
  nextAction: MeetingHumanScoreAspectSchema,
  insight: MeetingHumanScoreAspectSchema,
});
export type MeetingHumanScoreAspects = z.infer<typeof MeetingHumanScoreAspectsSchema>;

/** POST /api/meeting-scoring/{tenantId}/{meetingId} の本文 */
export const SaveMeetingHumanScoreRequestSchema = z.object({
  aspects: MeetingHumanScoreAspectsSchema,
  /**
   * 画面を開いたときの自分の最新の scoredAt（まだ採点していなければ null）。
   * 別のタブで先に直していたら 409 にする（知らないまま上書きして、理由の要る比較をすり抜けないため）。
   */
  baseScoredAt: z.string().nullable(),
});
export type SaveMeetingHumanScoreRequest = z.infer<typeof SaveMeetingHumanScoreRequestSchema>;

/** save = その人の最初の採点 / revise = 訂正 */
export const MeetingHumanScoreKindSchema = z.enum(["save", "revise"]);
export type MeetingHumanScoreKind = z.infer<typeof MeetingHumanScoreKindSchema>;

/** 採点したときに見えていた AI の点（あとで AI が付け直しても、何と比べて付けたかが分かるように残す） */
export const MeetingHumanScoreAiSnapshotSchema = z.object({
  total: z.number(),
  scoredAt: z.string(),
  levels: z.record(MeetingScoreAspectIdSchema, MeetingScoreLevelSchema.nullable()),
});
export type MeetingHumanScoreAiSnapshot = z.infer<typeof MeetingHumanScoreAiSnapshotSchema>;

/** 人の採点1回ぶん（画面に返す形。表のキーは持たない） */
export const MeetingHumanScoreSchema = z.object({
  tenantId: z.string(),
  meetingId: z.string(),
  /** 採点した人（Cognito sub。表示名は画面で引く） */
  scorerSub: z.string(),
  /** ISO 8601・UTC */
  scoredAt: z.string(),
  kind: MeetingHumanScoreKindSchema,
  /** その人の何回目の採点か（1 から） */
  revision: z.number().int(),
  aspects: MeetingHumanScoreAspectsSchema,
  /** 合計（A・B は採点した時点の記録から数えたもの。式は AI と同じ composeMeetingScoreTotal） */
  total: z.number(),
  coveragePoints: z.number(),
  hasReview: z.boolean(),
  /** 採点したときの AI の点。AI がまだ採点していなければ null */
  ai: MeetingHumanScoreAiSnapshotSchema.nullable(),
  /** 採点した時点の記録の指紋（AI の inputFingerprint と同じ作り方） */
  recordFingerprint: z.string(),
  /** 採点基準の版（MEETING_SCORE_RUBRIC_VERSION） */
  rubricVersion: z.string(),
});
export type MeetingHumanScore = z.infer<typeof MeetingHumanScoreSchema>;

/**
 * 表 MeetingHumanScores の行（Admin 所有）
 * PK: TENANT#{tenantId}
 * SK: LATEST#MEETING#{meetingId}#BY#{scorerSub}（人ごとの最新） / HIST#MEETING#{meetingId}#BY#{scorerSub}#AT#{scoredAt}（履歴）
 *     OPEN#MEETING#{meetingId}#AT#{openedAt}#BY#{scorerSub}（詳細を開いた記録）
 * 先頭を役割で分けるのは、一覧が LATEST# だけ・詳細が HIST# だけを begins_with で読めるようにするため
 */
export const DDBMeetingHumanScoreSchema = MeetingHumanScoreSchema.extend({
  PK: z.string(),
  SK: z.string(),
});
export type DDBMeetingHumanScore = z.infer<typeof DDBMeetingHumanScoreSchema>;

/** 一覧の行の、人1人ぶん（最新） */
export const MeetingScoringHumanLatestSchema = z.object({
  scorerSub: z.string(),
  scoredAt: z.string(),
  total: z.number(),
  revision: z.number().int(),
  levels: z.record(MeetingScoreAspectIdSchema, MeetingScoreLevelSchema),
});
export type MeetingScoringHumanLatest = z.infer<typeof MeetingScoringHumanLatestSchema>;

/** 一覧の1行（公開の商談記録で、AI が採点したもの） */
export const MeetingScoringRowSchema = z.object({
  meetingId: z.string(),
  title: z.string(),
  /** 商談日 "YYYY-MM-DD" */
  meetingDate: z.string(),
  hasReview: z.boolean(),
  ai: z.object({
    total: z.number(),
    status: MeetingScoreStatusSchema,
    levels: z.record(MeetingScoreAspectIdSchema, MeetingScoreLevelSchema.nullable()),
  }),
  /** 採点した人ごとの最新（古い順） */
  humans: z.array(MeetingScoringHumanLatestSchema),
});
export type MeetingScoringRow = z.infer<typeof MeetingScoringRowSchema>;

/** GET /api/meeting-scoring?targetTenantId=&days= */
export const MeetingScoringListResponseSchema = z.object({
  tenantId: z.string(),
  days: z.number().int(),
  rows: z.array(MeetingScoringRowSchema),
});
export type MeetingScoringListResponse = z.infer<typeof MeetingScoringListResponseSchema>;

/** 一覧の期間（作成日で数える）の選択肢 */
export const MEETING_SCORING_DAYS = [30, 90] as const;

export const MeetingScoringRecordLineSchema = z.object({ label: z.string().nullable(), value: z.string() });

/** GET /api/meeting-scoring/{tenantId}/{meetingId} */
export const MeetingScoringDetailResponseSchema = z.object({
  tenantId: z.string(),
  meetingId: z.string(),
  title: z.string(),
  meetingDate: z.string(),
  presetName: z.string().nullable(),
  /** 商談記録と振り返り（AI に渡す本文と同じ行。文字起こし・音声ログ・対話ログは持たない） */
  record: z.array(MeetingScoringRecordLineSchema),
  review: z.array(MeetingScoringRecordLineSchema),
  /** A（今の記録から数えたもの） */
  coverage: MeetingScoreCoverageSchema,
  /** B */
  hasReview: z.boolean(),
  recordFingerprint: z.string(),
  /** AI の最新の点 */
  ai: z
    .object({
      total: z.number(),
      scoredAt: z.string(),
      status: MeetingScoreStatusSchema,
      model: z.string().nullable(),
      aspects: z.record(MeetingScoreAspectIdSchema, MeetingScoreAspectSchema),
      /** AI が採点したあとに記録が変わった（AI が読んだものと今の記録が違う） */
      recordChanged: z.boolean(),
    })
    .nullable(),
  /** 全員の採点の履歴（古い順） */
  history: z.array(MeetingHumanScoreSchema),
});
export type MeetingScoringDetailResponse = z.infer<typeof MeetingScoringDetailResponseSchema>;
